import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import TransactionModal from './TransactionModal';
import DashboardSettingsModal from './DashboardSettingsModal';
import AccountsManagerModal from './AccountsManagerModal';
import api from '../api';
import InvestmentsSection from './InvestmentsSection';
import LendBorrow from './LendBorrow';
import { useCurrency } from '../CurrencyContext';
import { AuthContext } from '../AuthContext';
import { useAddTransaction, usePrefetchTransactions, useAllTransactions, useAccounts, useConsolidatedAccounts, useSplitGroups, useProfileSettings, QUERY_KEYS } from '../TransactionService';
import './AddTransaction.css';
import { useTheme } from '../ThemeContext';

// ── Static data ──────────────────────────────────────────────

const DEBIT_CATEGORIES = {
  Needs: [
    { id: 'rent', icon: '🏠', label: 'Rent' },
    { id: 'groceries', icon: '🛒', label: 'Groceries' },
    { id: 'utilities', icon: '⚡', label: 'Utilities' },
    { id: 'metro', icon: '🚇', label: 'Metro' },
    { id: 'health', icon: '💊', label: 'Health' },
    { id: 'insurance', icon: '🔒', label: 'Insurance' },
    { id: 'emi', icon: '🏦', label: 'EMI / Loan' },
    { id: 'food_need', icon: '🍱', label: 'Food' },
  ],
  Wants: [
    { id: 'dining', icon: '🍔', label: 'Dining Out' },
    { id: 'food', icon: '🍱', label: 'Food' },
    { id: 'subscriptions', icon: '📱', label: 'Subscriptions' },
    { id: 'shopping', icon: '🛍️', label: 'Shopping' },
    { id: 'entertainment', icon: '🎬', label: 'Entertainment' },
    { id: 'travel', icon: '✈️', label: 'Travel' },
    { id: 'hobbies', icon: '🎯', label: 'Hobbies' },
    { id: 'personal_care', icon: '🌸', label: 'Personal Care' },
  ],
  Financial: [
    { id: 'debt', icon: '💸', label: 'Debt Repayment' },
    { id: 'savings', icon: '💰', label: 'Savings' },
    { id: 'investments', icon: '📈', label: 'Investments' },
    { id: 'transfer', icon: '🔄', label: 'Transfer' },
  ],
};

const CREDIT_CATEGORIES = {
  Income: [
    { id: 'salary', icon: '💵', label: 'Salary' },
    { id: 'freelance', icon: '💻', label: 'Freelance' },
    { id: 'business', icon: '🏢', label: 'Business' },
    { id: 'interest', icon: '🏦', label: 'Interest' },
    { id: 'dividends', icon: '📊', label: 'Dividends' },
    { id: 'rental_income', icon: '🏠', label: 'Rental Income' },
  ],
  Reimbursement: [
    { id: 'refund', icon: '↩️', label: 'Refund' },
    { id: 'cashback', icon: '🎁', label: 'Cashback' },
    { id: 'reimbursement', icon: '🧾', label: 'Reimbursement' },
    { id: 'split_receive', icon: '🤝', label: 'Split Received' },
  ],
  Other: [
    { id: 'gift_received', icon: '🎀', label: 'Gift' },
    { id: 'sold_item', icon: '🏷️', label: 'Sold Item' },
    { id: 'other_credit', icon: '💚', label: 'Other' },
  ],
};

// All account types (used for display in cards scroller)
const ACCOUNT_TYPES = [
  { type: 'debit', icon: '💳', label: 'Debit Card', mechanism: 'pay_first', color: '#e0f2fe' },
  { type: 'credit', icon: '💳', label: 'Credit Card', mechanism: 'pay_later', color: '#fce7f3' },
  { type: 'bank', icon: '🏦', label: 'Bank Account', mechanism: 'pay_first', color: '#dbeafe' },
  { type: 'cash', icon: '💵', label: 'Cash', mechanism: 'pay_first', color: '#dcfce7' },
  { type: 'upi', icon: '📱', label: 'UPI', mechanism: 'pay_first', color: '#ffedd5' },
  { type: 'prepaid_wallet', icon: '👛', label: 'Wallet', mechanism: 'pay_first', color: '#fef9c3' },
  { type: 'metro_card', icon: '🚇', label: 'Metro Card', mechanism: 'pay_first', color: '#e0e7ff' },
  { type: 'food_card', icon: '🍱', label: 'Food Card', mechanism: 'pay_first', color: '#ffe4e6' },
  { type: 'other', icon: '📦', label: 'Other', mechanism: 'pay_first', color: '#f3e8ff' },
];

// Types available in the "Add Instrument" form (Cash is auto-created, not user-added)
const ADDABLE_ACCOUNT_TYPES = ACCOUNT_TYPES.filter(t => t.type !== 'cash');

const ACCOUNT_META = Object.fromEntries(ACCOUNT_TYPES.map(t => [t.type, t]));

const todayStr = () => new Date().toISOString().slice(0, 10);
const nowTimeStr = () => new Date().toTimeString().slice(0, 5);

// ── Component ──────────────────────────────────────────────

// ── Scheduled transaction recurrence options ─────────────
const RECURRENCE_TYPES = [
  { id: 'one_time',              icon: '🗓️', label: 'One-time',        sub: 'Single future date',         color: '#ede9fe', accent: '#7c3aed' },
  { id: 'daily',                 icon: '☀️', label: 'Daily',           sub: 'Every single day',            color: '#fef9c3', accent: '#ca8a04' },
  { id: 'weekly',                icon: '📅', label: 'Weekly',          sub: 'Pick a day each week',        color: '#dbeafe', accent: '#2563eb' },
  { id: 'biweekly',              icon: '🔁', label: 'Bi-weekly',       sub: 'Every 2 weeks',               color: '#dcfce7', accent: '#16a34a' },
  { id: 'monthly_fixed',         icon: '📆', label: 'Monthly (Date)',  sub: 'Same date each month',        color: '#fce7f3', accent: '#db2777' },
  { id: 'monthly_first',         icon: '1️⃣', label: 'Monthly 1st',    sub: 'First day of month',          color: '#e0f2fe', accent: '#0284c7' },
  { id: 'monthly_last',          icon: '🔚', label: 'Monthly Last',    sub: 'Last day of month',           color: '#ffe4e6', accent: '#e11d48' },
  { id: 'monthly_first_working', icon: '💼', label: '1st Weekday',     sub: 'First Mon–Fri of month',      color: '#f0fdf4', accent: '#15803d' },
  { id: 'monthly_last_working',  icon: '🏁', label: 'Last Weekday',    sub: 'Last Mon–Fri of month',       color: '#f1f5f9', accent: '#475569' },
  { id: 'quarterly',             icon: '📊', label: 'Quarterly',       sub: 'Every 3 months',              color: '#fff7ed', accent: '#ea580c' },
  { id: 'half_yearly',           icon: '📈', label: 'Half-yearly',     sub: 'Every 6 months',              color: '#fdf4ff', accent: '#9333ea' },
  { id: 'yearly',                icon: '🎂', label: 'Yearly',          sub: 'Once a year',                 color: '#ecfdf5', accent: '#059669' },
];

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTHS_LIST  = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ── Mode definitions ──────────────────────────────────────
const MODES = [
  {
    id: 'add_transaction',
    icon: '➕',
    label: 'Add Transaction',
    sub: 'Record a debit or credit',
    color: '#ede9fe',
    accent: '#6c63ff',
  },
  {
    id: 'pay_cc_bill',
    icon: '💳',
    label: 'Pay Credit Card Bill',
    sub: 'Clear your card outstanding',
    color: '#fce7f3',
    accent: '#db2777',
  },
  {
    id: 'bank_transfer',
    icon: '🔄',
    label: 'Transaction Transfers',
    sub: 'Move money between accounts',
    color: '#dbeafe',
    accent: '#2563eb',
  },
  {
    id: 'scheduled',
    icon: '🗓️',
    label: 'Scheduled Transactions',
    sub: 'Set up recurring payments',
    color: '#dcfce7',
    accent: '#16a34a',
  },
  {
    id: 'lend_borrow',
    icon: '🤝',
    label: 'Lend and Borrow',
    sub: 'Track loans and debts',
    color: '#fff7ed',
    accent: '#ea580c',
  },
  {
    id: 'investments',
    icon: '📈',
    label: 'Investments',
    sub: 'Stock markets and policies',
    color: '#f0fdfa',
    accent: '#0d9488',
  },
];

class InlineErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, errorInfo) { console.error(error, errorInfo); }
  render() {
    if (this.state.hasError) {
      return <div style={{padding: 20, color: 'red'}}>UI Crashed: {this.state.error?.toString()}</div>;
    }
    return this.props.children;
  }
}

export default function AddTransaction({ 
  propDashboardId, 
  onDashboardChange, 
  propActiveMode = null, 
  onActiveModeChange, 
  onAccountClick,
  spendingHeadline,      // New: overall spending sum
  balance,               // New: overall balance
  owed,                  // New: overall debt
  income,                // New: overall income
  onShowBreakdown,       // New: trigger breakdown modal
  countingMode,          // New: 'items' | 'bills'
  setCountingMode        // New: toggle mode
}) {
  const { user } = useContext(AuthContext);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const queryClient = useQueryClient();
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';

  // ── Data state ──
  // ── Data state (Manual fallbacks for legacy logic) ──
  const [dashboards, setDashboards] = useState(() => {
    try {
      const saved = localStorage.getItem('budgetify_dashboards');
      return saved ? JSON.parse(saved) : [];
    } catch (_) { return []; }
  });
  const [accounts, setAccounts] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [showDashboardSettings, setShowDashboardSettings] = useState(false);
  const [showAccountsMgr, setShowAccountsMgr] = useState(false);

  // ── Form state ──
  const [dashboardId, setDashboardId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [splitId, setSplitId] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [txDate, setTxDate] = useState(todayStr());
  const [txTime, setTxTime] = useState(nowTimeStr());
  const [description, setDescription] = useState('');
  const [txType, setTxType] = useState('debit'); // 'debit' | 'credit'

  // ── TanStack Query Shared State ──
  const { data: qTransactions } = useAllTransactions(dashboardId);
  const { data: qAccounts } = useAccounts(dashboardId);
  const { data: qConsolidatedAccounts } = useConsolidatedAccounts(dashboardId);
  const { data: qSplitGroups } = useSplitGroups(dashboardId);
  
  // Sync query data to local state for compatibility
  useEffect(() => {
    if (qTransactions) setAllTransactions(qTransactions);
    if (qAccounts) {
      let list = qAccounts || [];
      if (!list.some(a => a.account_type === 'cash')) {
        list = [{ id: '__cash__', name: 'Cash', account_type: 'cash', mechanism: 'pay_first', current_balance: 0, _virtual: true }, ...list];
      }
      setAccounts(list);
    }
    if (qConsolidatedAccounts) setConsolidatedAccounts(qConsolidatedAccounts);
  }, [qTransactions, qAccounts, qConsolidatedAccounts]);


  // ── UI state ──
  const [activeMode, setActiveMode] = useState(propActiveMode);
  const [loadingBoards, setLoadingBoards] = useState(() => {
    // If we have cached dashboards, don't show a blocking loader, fetch in background
    const saved = localStorage.getItem('budgetify_dashboards');
    return !saved;
  });

  const [submitting, setSubmitting] = useState(false);

  // Budget Modal State

  const [success, setSuccess] = useState(false);
  const [flashMsg, setFlashMsg] = useState('');
  const [error, setError] = useState('');
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [creatingAcc, setCreatingAcc] = useState(false);
  const [creatingSplit, setCreatingSplit] = useState(false);

  // ── Inline create state ──
  const [showNewBoard, setShowNewBoard] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [showNewAcc, setShowNewAcc] = useState(false);
  const [editingAccId, setEditingAccId] = useState(null);
  const [newAcc, setNewAcc] = useState({
    name: '', account_type: 'debit', initial_balance: '',
    bank_name: '', card_number_last4: '', card_network: '',
    expiry_month: '', expiry_year: '', billing_due_day: '',
    notification_days_before: '3', credit_limit: '', provider: '', auto_debit: false, upi_id: '',
  });
  const [userDefaults, setUserDefaults] = useState({});
  const [tags, setTags] = useState([]);
  const [tagId, setTagId] = useState('');
  const [showNewTag, setShowNewTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#6c63ff');
  const [tagMgrOpen, setTagMgrOpen] = useState(false);

  // ── Validation/Scroll state ──
  const [missingPast, setMissingPast] = useState([]);
  // Mobile-only: toggles the floating checklist sheet
  const [showMobileChecklist, setShowMobileChecklist] = useState(false);
  const [closingMobileChecklist, setClosingMobileChecklist] = useState(false);

  const closeMobileChecklist = useCallback(() => {
    setClosingMobileChecklist(true);
    setTimeout(() => {
      setShowMobileChecklist(false);
      setClosingMobileChecklist(false);
    }, 240);
  }, []);
  const formRefs = {
    amount: useRef(null),
    dashboard: useRef(null),
    category: useRef(null),
    account: useRef(null),
    when: useRef(null),
    note: useRef(null),
  };
  
  // Sync propActiveMode -> activeMode
  useEffect(() => {
    // Only adopt a new mode from props if it's explicitly provided.
    // This prevents the component from resetting itself during parent re-renders
    // if the prop is occasionally missing or slow to propagate.
    if (propActiveMode !== null && propActiveMode !== undefined) {
      setActiveMode(propActiveMode);
    }
  }, [propActiveMode]);

  // IMPORTANT: We REMOVED the automatic broadcasting useEffect here.
  // Broadcasting the local state back to the parent automatically is prone to
  // race conditions (the "1-2s flash away" issue).
  // Instead, we will EXPLICITLY call onActiveModeChange only when the user
  // interacts with navigation buttons (e.g., the Back button).


  useEffect(() => {
    if (propDashboardId && propDashboardId !== dashboardId) {
      setDashboardId(propDashboardId);
    }
  }, [propDashboardId, dashboardId]);

  useEffect(() => {
    if (dashboardId && onDashboardChange) {
      onDashboardChange(dashboardId);
    }
  }, [dashboardId, onDashboardChange]);

  useEffect(() => {
    if (!activeMode) {
      setMissingPast([]);
      return;
    }

    const onScroll = () => {
      const mid = window.innerHeight / 2.3; // threshold slightly above center
      const missing = [];

      const check = (id, val, ref) => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        // If the bottom of the card has passed the 'mid' line and value is empty
        if (rect.bottom < mid && !val.toString().trim()) {
          missing.push(id);
        }
      };

      check('Amount', amount, formRefs.amount);
      check('Dashboard', dashboardId, formRefs.dashboard);
      check('Category', category, formRefs.category);
      check('Account', accountId, formRefs.account);
      check('Date & Time', txDate && txTime, formRefs.when);
      check('Note', description, formRefs.note);

      setMissingPast(missing);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // Run once on mount/mode toggle
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [activeMode, amount, dashboardId, category, accountId, txDate, txTime, description, formRefs.amount, formRefs.dashboard, formRefs.category, formRefs.account, formRefs.when, formRefs.note]);
  const [showNewSplit, setShowNewSplit] = useState(false);
  const [newSplitName, setNewSplitName] = useState('');

  // ── Right panel / modal state ──

  const [cardModalAcc, setCardModalAcc] = useState(null);   // account to show in modal
  const [cardModalTxns, setCardModalTxns] = useState([]);
  const [cardModalLoading, setCardModalLoading] = useState(false);
  const [cardFlipped, setCardFlipped] = useState(false);
  const [editTx, setEditTx] = useState(null);
  const [payCCCardId, setPayCCCardId] = useState("");
  const [payCCSourceId, setPayCCSourceId] = useState("");
  const [transferFromId, setTransferFromId] = useState("");
  const [transferToId, setTransferToId] = useState("");

  // ── Scheduled transaction state ──
  const [schedStep, setSchedStep] = useState(1);
  const [schedRecurrence, setSchedRecurrence] = useState('');
  const [schedDayOfWeek, setSchedDayOfWeek] = useState(0);
  const [schedDayOfMonth, setSchedDayOfMonth] = useState(1);
  const [schedMonth, setSchedMonth] = useState(1);
  const [schedStartDate, setSchedStartDate] = useState(todayStr());
  const [schedRunTime, setSchedRunTime] = useState('09:00');
  const [schedEndType, setSchedEndType] = useState('never');
  const [schedEndDate, setSchedEndDate] = useState('');
  const [schedMaxOcc, setSchedMaxOcc] = useState('');
  const [scheduledList, setScheduledList] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(false);
  const [editingSchedId, setEditingSchedId] = useState(null);

  // ── Split groups page state ──
  const [sgView, setSgView] = useState('list');          // 'list' | 'detail'
  const [activeSg, setActiveSg] = useState(null);        // selected group object
  const [sgMembers, setSgMembers] = useState([]);
  const [sgTxns, setSgTxns] = useState([]);
  const [sgLoadingDetail, setSgLoadingDetail] = useState(false);
  const [sgShowInvite, setSgShowInvite] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joiningGroup, setJoiningGroup] = useState(false);
  const [showJoinInput, setShowJoinInput] = useState(false);

  // Track scroll specifically for showing "Fill this!" alerts
  const [atScrolled, setAtScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      // Show alerts after 30px scroll
      if (window.scrollY > 30) {
        setAtScrolled(true);
      } else {
        setAtScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Reset atScrolled and scroll to top when switching modes
  useEffect(() => {
    if (activeMode) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      setAtScrolled(false);
    } else {
      setAtScrolled(false);
    }
    setShowMobileChecklist(false);
  }, [activeMode]);

  // TanStack Query Mutation for adding transactions
  const addTransactionMutation = useAddTransaction();
  const prefetchTransactions = usePrefetchTransactions(dashboardId);

  // Prefetch data when dashboard changes or on initial mount
  useEffect(() => {
    if (dashboardId) {
      prefetchTransactions();
    }
  }, [dashboardId, prefetchTransactions]);

  const { data: profileSettings } = useProfileSettings();

  useEffect(() => {
    if (profileSettings) {
      setUserDefaults(prev => ({ ...prev, ...profileSettings }));
      if (profileSettings.default_category && !category) {
        setCategory(profileSettings.default_category);
      }
    }
  }, [profileSettings, category]);



  // Clear category when switching tx type
  useEffect(() => { setCategory(''); }, [txType]);

  // Load scheduled transactions list when entering that mode
  useEffect(() => {
    if (activeMode === 'scheduled' && dashboardId) {
      fetchScheduledTransactions(dashboardId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMode, dashboardId]);

  // Load user defaults FIRST, then dashboards ──
  useEffect(() => { 
    fetchDashboards();
  }, []);



  // Fetch all transactions for the dashboard (for summary)
  const fetchAllTransactions = useCallback(async (dbId) => {
    try {
      const { data } = await api.get(`/api/transactions?dashboard_id=${dbId}&limit=200`);
      setAllTransactions(data?.transactions || data || []);
    } catch (_) { setAllTransactions([]); }
  }, []);

  const fetchTags = useCallback(async (dbId) => {
    try {
      const { data } = await api.get(`/api/tags?dashboard_id=${dbId}`);
      setTags(data || []);
    } catch (_) { setTags([]); }
  }, []);

  // ── Load splits when dashboard changes (Accounts and Transactions handled by hooks) ──
  useEffect(() => {
    if (dashboardId) {
      fetchTags(dashboardId);
    } else {
      setAccounts([]);
      setAccountId('');
      setAllTransactions([]);
      setTags([]);
      setTagId('');
    }
  }, [dashboardId, userDefaults, fetchTags]);


  async function fetchDashboards() {
    setLoadingBoards(true);
    try {
      const { data } = await api.get('/api/dashboards');
      let list = data || [];
      // Auto-create a default "Personal" dashboard if none exist
      if (list.length === 0) {
        try {
          const { data: newBoard } = await api.post('/api/dashboards', { name: 'Personal' });
          list = [newBoard];
        } catch (_) { }
      }
      setDashboards(list);
      localStorage.setItem('budgetify_dashboards', JSON.stringify(list));
    } catch (_) { }
    setLoadingBoards(false);
  }

  // Manual reload for the form: pulls a fresh dashboards list from the server
  // and invalidates every React-Query cache the form reads from. Useful when
  // the user knows something changed elsewhere (another device, browser tab)
  // and doesn't want to wait for the next focus-based sync to catch up.
  const [reloading, setReloading] = useState(false);
  const handleReloadFormData = async () => {
    if (reloading) return;
    setReloading(true);
    try {
      await Promise.all([
        fetchDashboards(),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allTransactions(dashboardId) }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts(dashboardId) }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.consolidatedAccounts(dashboardId) }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.splitGroups(dashboardId) }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dashboardSummary(dashboardId) }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.profileSettings() }),
      ]);
    } finally {
      // Brief minimum spin so the icon doesn't flicker on fast networks.
      setTimeout(() => setReloading(false), 400);
    }
  };

  // ── Reactive Auto-Selection ──
  useEffect(() => {
    if (!dashboards.length) return;
    
    // DASHBOARD
    if (!dashboardId) {
      if (userDefaults.default_dashboard_id && dashboards.find(d => d.id === userDefaults.default_dashboard_id)) {
        setDashboardId(userDefaults.default_dashboard_id);
      } else if (dashboards.length === 1) {
        setDashboardId(dashboards[0].id);
      }
    }

    // ACCOUNT
    if (accounts.length > 0) {
      const savedDefAcc = userDefaults.default_account_id;
      // If nothing selected, or current selection not in list, try to select
      if (!accountId || !accounts.find(a => a.id === accountId)) {
        if (savedDefAcc && accounts.find(a => a.id === savedDefAcc)) {
          setAccountId(savedDefAcc);
        } else if (accounts.length === 1) {
          setAccountId(accounts[0].id);
        } else if (accounts.length === 2 && accounts.some(a => a._virtual)) {
          const real = accounts.find(a => !a._virtual);
          if (real) setAccountId(real.id);
        } else if (!accountId) {
          setAccountId(accounts[0].id);
        }
      }
    }

    // SPLIT GROUP
    if ((qSplitGroups || []).length > 0) {
      const savedDefSplit = userDefaults.default_split_group_id;
      if (!splitId || !(qSplitGroups || []).find(g => g.id === splitId)) {
        if (savedDefSplit && (qSplitGroups || []).find(g => g.id === savedDefSplit)) {
          setSplitId(savedDefSplit);
        } else if ((qSplitGroups || []).length === 1) {
          setSplitId((qSplitGroups || [])[0].id);
        }
      }
    }
  }, [dashboards, accounts, qSplitGroups, userDefaults, dashboardId, accountId, splitId]);

  // Fetch transactions for a specific account (for card modal)
  async function fetchCardTransactions(accId) {
    setCardModalLoading(true);
    try {
      const { data } = await api.get(`/api/transactions?account_id=${accId}&limit=50`);
      setCardModalTxns(data?.transactions || data || []);
    } catch (_) { setCardModalTxns([]); }
    setCardModalLoading(false);
  }

  function openCardModal(acc) {
    setCardModalAcc(acc);
    setCardFlipped(false);
    setCardModalTxns([]);
    fetchCardTransactions(acc.id);
  }

  function closeCardModal() {
    setCardModalAcc(null);
    setCardModalTxns([]);
    setCardFlipped(false);
  }

  async function handleCreateDashboard() {
    if (!newBoardName.trim()) return;
    setCreatingBoard(true);
    try {
      const { data } = await api.post('/api/dashboards', { name: newBoardName.trim() });
      setDashboards(prev => [...prev, data]);
      setDashboardId(data.id);
      setNewBoardName('');
      setShowNewBoard(false);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not create dashboard');
    } finally {
      setCreatingBoard(false);
    }
  }

  async function handleCreateAccount() {
    if (!newAcc.name.trim() || !dashboardId) return;
    setCreatingAcc(true);
    const meta = ACCOUNT_META[newAcc.account_type];
    const payload = {
      dashboard_id: dashboardId,
      name: newAcc.name.trim(),
      account_type: newAcc.account_type,
      mechanism: meta?.mechanism || 'pay_first',
      initial_balance: parseFloat(newAcc.initial_balance || 0),
    };
    // Context-aware fields
    if (newAcc.bank_name !== undefined) payload.bank_name = newAcc.bank_name ? newAcc.bank_name.trim() : '';
    if (newAcc.card_number_last4 !== undefined) payload.card_number_last4 = newAcc.card_number_last4 ? newAcc.card_number_last4.trim() : '';
    if (newAcc.card_network !== undefined) payload.card_network = newAcc.card_network ? newAcc.card_network.trim() : '';
    if (newAcc.expiry_month !== undefined) payload.expiry_month = newAcc.expiry_month ? parseInt(newAcc.expiry_month) : null;
    if (newAcc.expiry_year !== undefined) payload.expiry_year = newAcc.expiry_year ? parseInt(newAcc.expiry_year) : null;
    if (newAcc.billing_due_day !== undefined) payload.billing_due_day = newAcc.billing_due_day ? parseInt(newAcc.billing_due_day) : null;
    if (newAcc.notification_days_before !== undefined) payload.notification_days_before = newAcc.notification_days_before ? parseInt(newAcc.notification_days_before) : 3;
    if (newAcc.credit_limit !== undefined) payload.credit_limit = newAcc.credit_limit ? parseFloat(newAcc.credit_limit) : null;
    if (newAcc.provider !== undefined) payload.provider = newAcc.provider ? newAcc.provider.trim() : '';
    if (newAcc.upi_id !== undefined) payload.upi_id = newAcc.upi_id ? newAcc.upi_id.trim() : '';
    payload.auto_debit = !!newAcc.auto_debit;

    try {
      if (editingAccId) {
        await api.patch(`/api/accounts/${editingAccId}`, payload);
        const ObjectCopy = { ...payload };
        setAccounts(prev => prev.map(a => a.id === editingAccId ? { ...a, ...ObjectCopy } : a));
      } else {
        const { data } = await api.post('/api/accounts', payload);
        setAccounts(prev => [...prev, data]);
        setAccountId(data.id);
      }

      setNewAcc({
        name: '', account_type: 'debit', initial_balance: '',
        bank_name: '', card_number_last4: '', card_network: '',
        expiry_month: '', expiry_year: '', billing_due_day: '',
        notification_days_before: '3', credit_limit: '', provider: '', auto_debit: false, upi_id: '',
      });
      setShowNewAcc(false);
      setEditingAccId(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not ' + (editingAccId ? 'update' : 'create') + ' account');
    } finally {
      setCreatingAcc(false);
    }
  }

  async function handlePayCCBill() {
    if (!payCCCardId || !payCCSourceId || !amount || parseFloat(amount) <= 0 || !dashboardId || !description.trim()) {
      setError("Please fill all required fields (including a note).");
      return;
    }
    setSubmitting(true);
    try {
      let finalSourceId = payCCSourceId;
      if (payCCSourceId === '__cash__') {
        const { data: cashAcc } = await api.post('/api/accounts', {
          dashboard_id: dashboardId,
          name: 'Cash',
          account_type: 'cash',
          mechanism: 'pay_first',
          initial_balance: 0,
        });
        finalSourceId = cashAcc.id;
        setAccounts(prev => prev.map(a => a.id === '__cash__' ? cashAcc : a));
        setPayCCSourceId(finalSourceId);
      }

      await api.post('/api/transactions/transfer', {
        dashboard_id: dashboardId,
        from_account_id: finalSourceId,
        to_account_id: payCCCardId,
        amount: parseFloat(amount),
        transaction_date: txDate,
        description: description,
        category: 'Credit Card Payment'
      });

      setSuccess(true);
      setFlashMsg("Credit card bill payment recorded!");
      setAmount('');
      setDescription('');
      setPayCCCardId("");
      setPayCCSourceId("");
      setActiveMode(null);
      if (onActiveModeChange) onActiveModeChange(null);

      // Refresh global cache
      if (dashboardId) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allTransactions(dashboardId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dashboardSummary(dashboardId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts(dashboardId) });
        fetchAllTransactions(dashboardId);
      }
      const accResp = await api.get('/api/accounts?dashboard_id=' + dashboardId);
      if (accResp.data) {
         let newAccounts = accResp.data;
         if (!newAccounts.some(a => a.account_type === 'cash')) {
            newAccounts.unshift({ id: '__cash__', name: 'Cash', account_type: 'cash', mechanism: 'pay_first', current_balance: 0, _virtual: true });
         }
         setAccounts(newAccounts);
      }
      
      setTimeout(() => { setSuccess(false); setFlashMsg(''); }, 2800);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to process CC payment");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleTransfer() {
    if (!transferFromId || !transferToId || !amount || parseFloat(amount) <= 0 || !dashboardId) {
      setError("Please fill all required fields.");
      return;
    }
    if (transferFromId === transferToId) {
      setError("Source and destination accounts must be different.");
      return;
    }
    if (!description.trim()) {
      setError("Please provide a note for this transfer.");
      return;
    }
    setSubmitting(true);
    try {
      let finalFrom = transferFromId;
      let finalTo = transferToId;

      if (transferFromId === '__cash__' || transferToId === '__cash__') {
        const { data: cashAcc } = await api.post('/api/accounts', {
          dashboard_id: dashboardId,
          name: 'Cash',
          account_type: 'cash',
          mechanism: 'pay_first',
          initial_balance: 0,
        });
        if (transferFromId === '__cash__') {
          finalFrom = cashAcc.id;
          setTransferFromId(finalFrom);
        }
        if (transferToId === '__cash__') {
          finalTo = cashAcc.id;
          setTransferToId(finalTo);
        }
        setAccounts(prev => prev.map(a => a.id === '__cash__' ? cashAcc : a));
      }

      await api.post('/api/transactions/transfer', {
        dashboard_id: dashboardId,
        from_account_id: finalFrom,
        to_account_id: finalTo,
        amount: parseFloat(amount),
        transaction_date: txDate,
        description: description,
      });
      setSuccess(true);
      setFlashMsg(`Transferred ${sym}${parseFloat(amount).toFixed(2)} successfully!`);
      setAmount('');
      setDescription('');
      setTransferFromId('');
      setTransferToId('');
      setActiveMode(null);
      if (onActiveModeChange) onActiveModeChange(null);
      if (dashboardId) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allTransactions(dashboardId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dashboardSummary(dashboardId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts(dashboardId) });
        fetchAllTransactions(dashboardId);
      }
      const accResp = await api.get('/api/accounts?dashboard_id=' + dashboardId);
      if (accResp.data) {
        let newAccounts = accResp.data;
        if (!newAccounts.some(a => a.account_type === 'cash')) {
          newAccounts.unshift({ id: '__cash__', name: 'Cash', account_type: 'cash', mechanism: 'pay_first', current_balance: 0, _virtual: true });
        }
        setAccounts(newAccounts);
      }
      setTimeout(() => { setSuccess(false); setFlashMsg(''); }, 2800);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to process transfer");
    } finally {
      setSubmitting(false);
    }
  }

  function resetSchedState() {
    setSchedStep(1);
    setSchedRecurrence('');
    setSchedDayOfWeek(0);
    setSchedDayOfMonth(1);
    setSchedMonth(1);
    setSchedStartDate(todayStr());
    setSchedRunTime('09:00');
    setSchedEndType('never');
    setSchedEndDate('');
    setSchedMaxOcc('');
    setEditingSchedId(null);
  }

  function handleEditScheduled(s) {
    setEditingSchedId(s.id);
    setAmount(s.amount.toString());
    setCategory(s.category);
    setDescription(s.description || '');
    setTxType(s.transaction_type || 'debit');
    setAccountId(s.account_id);
    setSplitId(s.split_group_id || '');
    
    setSchedRecurrence(s.recurrence_type);
    setSchedDayOfWeek(s.recurrence_day_of_week || 0);
    setSchedDayOfMonth(s.recurrence_day_of_month || 1);
    setSchedMonth(s.recurrence_month || 1);
    setSchedStartDate(s.start_date || todayStr());
    setSchedRunTime(s.run_time || '09:00');
    
    if (s.end_date) {
      setSchedEndType('date');
      setSchedEndDate(s.end_date);
    } else if (s.max_occurrences) {
      setSchedEndType('count');
      setSchedMaxOcc(s.max_occurrences.toString());
    } else {
      setSchedEndType('never');
    }
    
    setSchedStep(2); // Go to recurrence config step
  }

  async function fetchScheduledTransactions(did) {
    if (!did) return;
    setLoadingScheduled(true);
    try {
      const { data } = await api.get(`/api/scheduled-transactions?dashboard_id=${did}`);
      setScheduledList(data || []);
    } catch (_) { setScheduledList([]); }
    setLoadingScheduled(false);
  }

  async function handleCreateScheduled() {
    if (!amount || parseFloat(amount) <= 0) { setError('Enter a valid amount'); return; }
    if (!dashboardId) { setError('Select a dashboard'); return; }
    if (!accountId) { setError('Select an account'); return; }
    if (!category) { setError('Pick a category'); return; }
    if (!description.trim()) { setError('Please add a note for this schedule'); return; }
    setSubmitting(true);
    try {
      let finalAccountId = accountId;
      if (accountId === '__cash__') {
        const { data: cashAcc } = await api.post('/api/accounts', {
          dashboard_id: dashboardId,
          name: 'Cash',
          account_type: 'cash',
          mechanism: 'pay_first',
          initial_balance: 0,
        });
        finalAccountId = cashAcc.id;
        setAccounts(prev => prev.map(a => a.id === '__cash__' ? cashAcc : a));
        setAccountId(finalAccountId);
      }

      const payload = {
        dashboard_id: dashboardId,
        account_id: finalAccountId,
        split_group_id: splitId || null,
        amount: parseFloat(amount),
        transaction_type: txType,
        category,
        description,
        recurrence_type: schedRecurrence,
        recurrence_day_of_week: ['weekly', 'biweekly'].includes(schedRecurrence) ? schedDayOfWeek : null,
        recurrence_day_of_month: ['monthly_fixed', 'quarterly', 'half_yearly', 'yearly'].includes(schedRecurrence) ? schedDayOfMonth : null,
        recurrence_month: schedRecurrence === 'yearly' ? schedMonth : null,
        run_time: schedRunTime,
        start_date: schedStartDate,
        end_date: schedEndType === 'date' ? (schedEndDate || null) : null,
        max_occurrences: schedEndType === 'count' && schedMaxOcc ? parseInt(schedMaxOcc) : null,
        is_investment: category === 'investments'
      };

      if (editingSchedId) {
        await api.patch(`/api/scheduled-transactions/${editingSchedId}`, payload);
      } else {
        await api.post('/api/scheduled-transactions', payload);
      }

      setSuccess(true);
      setFlashMsg(editingSchedId ? 'Scheduled transaction updated!' : 'Scheduled transaction saved!');
      setAmount('');
      setCategory('');
      setDescription('');
      resetSchedState();
      setActiveMode(null);
      if (onActiveModeChange) onActiveModeChange(null);
      if (dashboardId) fetchScheduledTransactions(dashboardId);
      setTimeout(() => { setSuccess(false); setFlashMsg(''); }, 2800);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save scheduled transaction');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeleteScheduled(id) {
    try {
      await api.delete(`/api/scheduled-transactions/${id}`);
      setScheduledList(prev => prev.filter(s => s.id !== id));
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to delete');
    }
  }

  async function handleToggleScheduled(item) {
    try {
      await api.patch(`/api/scheduled-transactions/${item.id}`, { is_active: !item.is_active });
      setScheduledList(prev => prev.map(s => s.id === item.id ? { ...s, is_active: !s.is_active } : s));
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to update');
    }
  }

  async function openSplitGroupDetail(group) {
    setSgView('detail');
    setActiveSg(group);
    setSgShowInvite(false);
    setSgMembers([]);
    setSgTxns([]);
    setSgLoadingDetail(true);
    try {
      const [detailRes, txRes] = await Promise.all([
        api.get(`/api/split-groups/${group.id}`),
        api.get(`/api/split-groups/${group.id}/transactions`),
      ]);
      setActiveSg(detailRes.data);
      setSgMembers(detailRes.data.members || []);
      setSgTxns(txRes.data || []);
    } catch (_) {}
    setSgLoadingDetail(false);
  }

  async function handleGenerateSgInvite() {
    if (!activeSg) return;
    try {
      const { data } = await api.post(`/api/split-groups/${activeSg.id}/invite`);
      setActiveSg(prev => ({ ...prev, invite_code: data.invite_code }));
      setSgShowInvite(true);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to generate code');
    }
  }

  async function handleJoinGroup() {
    if (!joinCode.trim()) return;
    setJoiningGroup(true);
    try {
      const { data } = await api.post('/api/split-groups/join', { invite_code: joinCode.trim() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.splitGroups(dashboardId) });
      setJoinCode('');
      setShowJoinInput(false);
      setError('');
      setSuccess(true);
      setFlashMsg(`Joined "${data.name}" successfully!`);
      setTimeout(() => { setSuccess(false); setFlashMsg(''); }, 2800);
    } catch (e) {
      setError(e.response?.data?.error || 'Invalid invite code');
    } finally {
      setJoiningGroup(false);
    }
  }

  async function handleRemoveSgMember(memberId) {
    if (!activeSg) return;
    try {
      await api.delete(`/api/split-groups/${activeSg.id}/members/${memberId}`);
      setSgMembers(prev => prev.filter(m => m.id !== memberId));
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to remove member');
    }
  }

  async function handleCreateSplitGroup() {
    if (!newSplitName.trim() || !dashboardId) return;
    setCreatingSplit(true);
    try {
      const { data } = await api.post('/api/split-groups', {
        dashboard_id: dashboardId,
        name: newSplitName.trim(),
      });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.splitGroups(dashboardId) });
      setSplitId(data.id);
      setNewSplitName('');
      setShowNewSplit(false);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not create split group');
    } finally {
      setCreatingSplit(false);
    }
  }

  async function handleCreateTag() {
    if (!newTagName.trim() || !dashboardId) return;
    try {
      const { data } = await api.post('/api/tags', {
        dashboard_id: dashboardId,
        name: newTagName.trim(),
        color: newTagColor
      });
      setTags(prev => [...prev, data]);
      setTagId(data.id);
      setNewTagName('');
      setShowNewTag(false);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not create tag');
    }
  }

  async function toggleTagActive(tag) {
    try {
      await api.patch(`/api/tags/${tag.id}`, { is_active: !tag.is_active });
      setTags(prev => prev.map(t => t.id === tag.id ? { ...t, is_active: !t.is_active } : t));
    } catch (_) { }
  }

  async function handleDeleteTag(id) {
    if (!window.confirm('Delete this tag? Transactions using it will keep visible, but tag reference will be lost.')) return;
    try {
      await api.delete(`/api/tags/${id}`);
      setTags(prev => prev.filter(t => t.id !== id));
      if (tagId === id) setTagId('');
    } catch (_) { }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!amount || parseFloat(amount) <= 0) { setError('Enter a valid amount'); return; }
    if (!dashboardId) { setError('Select or create a dashboard'); return; }
    if (!accountId) { setError('Select or create an account'); return; }
    if (!category) { setError('Pick a category'); return; }
    if (!description.trim()) { setError('Please add a note/description'); return; }
    
    setSubmitting(true);
    try {
      // If virtual Cash is selected, create the real account first
      let realAccountId = accountId;
      if (accountId === '__cash__') {
        const { data: cashAcc } = await api.post('/api/accounts', {
          dashboard_id: dashboardId,
          name: 'Cash',
          account_type: 'cash',
          mechanism: 'pay_first',
          initial_balance: 0,
        });
        realAccountId = cashAcc.id;
        setAccounts(prev => prev.map(a => a.id === '__cash__' ? cashAcc : a));
        setAccountId(realAccountId);
      }

      // Prepare the transaction data
      const txData = {
        account_id: realAccountId,
        dashboard_id: dashboardId,
        split_group_id: splitId || null,
        tag_id: tagId || null,
        amount: parseFloat(amount),
        transaction_type: txType,
        category,
        transaction_date: txDate,
        description,
        is_investment: category === 'investments',
      };

      // Fire optimistic mutation
      await addTransactionMutation.mutateAsync(txData);

      // Success UI feedback
      const dirLabel = txType === 'credit' ? 'received' : 'spent';
      setSuccess(true);
      setFlashMsg(`${sym}${parseFloat(amount).toFixed(2)} ${dirLabel} · ${category} added!`);

      // Clear form
      setAmount('');
      setCategory('');
      setSplitId('');
      setTagId('');
      setDescription('');
      setTxDate(todayStr());
      setTxTime(nowTimeStr());

      // Bring the user back to the top of the Add Transaction page so the
      // form is in view and ready for the next entry.
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) { window.scrollTo(0, 0); }

      setTimeout(() => { setSuccess(false); setFlashMsg(''); }, 2800);
      
      // Local state update for accounts (optimistic manual touch for other parts of UI)
      setAccounts(prev => prev.map(a => {
        if (a.id !== realAccountId) return a;
        let delta;
        if (txType === 'credit') {
          delta = a.mechanism === 'pay_later' ? -parseFloat(amount) : parseFloat(amount);
        } else {
          delta = a.mechanism === 'pay_later' ? parseFloat(amount) : -parseFloat(amount);
        }
        return { ...a, current_balance: (parseFloat(a.current_balance) + delta).toFixed(2) };
      }));

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add transaction');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Helpers ──

  const CATEGORIES = txType === 'credit' ? CREDIT_CATEGORIES : DEBIT_CATEGORIES;
  const { currency } = useCurrency();
  const sym = currency.symbol;

  // ── Right panel computed data ──
  const [consolidatedAccounts, setConsolidatedAccounts] = useState([]);
  const realAccounts = accounts.filter(a => !a._virtual);

  const groupedInstruments = useMemo(() => {
    if (!accounts.length) return [];
    const accountMap = {};
    realAccounts.forEach(a => { accountMap[a.id] = a; });
    const usedAccountIds = new Set();
    const result = [];
    (consolidatedAccounts || []).forEach(ca => {
      const members = (ca.account_ids || []).map(id => accountMap[id]).filter(Boolean);
      if (members.length > 0) {
        members.forEach(m => usedAccountIds.add(m.id));
        const total = members.reduce((s, a) => {
          const b = parseFloat(a.current_balance || 0);
          return s + (a.mechanism === 'pay_later' ? -b : b);
        }, 0);
        result.push({ ...ca, isGroup: true, members, total });
      }
    });
    realAccounts.forEach(a => {
      if (!usedAccountIds.has(a.id)) result.push(a);
    });
    return result;
  }, [accounts, consolidatedAccounts, realAccounts]);
  // Accounts visible in the *adding* surfaces (Add Transaction picker, Pay
  // CC, Transfer, Schedule). Honors the per-account `hidden_from_add`
  // flag — hidden accounts still appear in dashboards and the manager
  // modal, just not while creating new transactions.
  const addableAccounts = accounts.filter(a => a._virtual || !a.hidden_from_add);
  const realAddableAccounts = realAccounts.filter(a => !a.hidden_from_add);
  const totalBalance = balance ?? realAccounts
    .filter(a => a.mechanism === 'pay_first')
    .reduce((s, a) => s + parseFloat(a.current_balance || 0), 0);
  const totalOwed = owed ?? realAccounts
    .filter(a => a.mechanism === 'pay_later')
    .reduce((s, a) => s + parseFloat(a.current_balance || 0), 0);
  const totalSpent = spendingHeadline ?? allTransactions
    .filter(t => t.transaction_type === 'debit' && t.category !== 'transfer')
    .reduce((s, t) => s + parseFloat(t.amount || 0), 0);
  const totalIncome = income ?? allTransactions
    .filter(t => t.transaction_type === 'credit' && t.category !== 'transfer')
    .reduce((s, t) => s + parseFloat(t.amount || 0), 0);
  // Credit cards / pay-later accounts that have outstanding balance are "bills"
  const billAccounts = realAccounts.filter(a => a.mechanism === 'pay_later' && parseFloat(a.current_balance || 0) > 0);

  // Context-aware fields for the new account form
  // Cash & Other: no extra fields at all — just name + balance
  const accType = newAcc.account_type;
  const isMinimal = ['cash', 'other'].includes(accType);
  const showBank = !isMinimal && ['debit', 'credit', 'bank', 'upi'].includes(accType);
  const showLast4 = !isMinimal && ['debit', 'credit'].includes(accType);
  const showNetwork = !isMinimal && ['debit', 'credit'].includes(accType);
  const showExpiry = !isMinimal && ['debit', 'credit'].includes(accType);
  const showDue = !isMinimal && accType === 'credit';
  const showNotify = !isMinimal && accType === 'credit';
  const showLimit = !isMinimal && accType === 'credit';
  const showAutoDebit = !isMinimal && accType === 'credit';
  const showProvider = !isMinimal && ['prepaid_wallet', 'food_card'].includes(accType);
  const showUpiId = !isMinimal && accType === 'upi';

  const setAccField = (field, val) => setNewAcc(p => ({ ...p, [field]: val }));

  return (

    <div className={`at-root ${activeMode ? 'at-mode-active' : ''} ${isDark ? 'at-dark' : ''}`}>



      {/* ─── Success toast (floating, polished) ─── */}
      {success && (
        <div className="at-toast at-toast--success" role="status" aria-live="polite">
          <span className="at-toast-check" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
          <div className="at-toast-body">
            <div className="at-toast-title">Transaction added</div>
            <div className="at-toast-sub">{flashMsg}</div>
          </div>
        </div>
      )}
      {error && (
        <div className="at-flash at-flash--error" onClick={() => setError('')}>
          ⚠ {error} <span className="at-flash-close">✕</span>
        </div>
      )}

      {/* Dashboard toolbar — lives outside at-two-col so grid/contents
          layout can't affect its visibility. Only shown on dashboard view. */}
      {!activeMode && (
        <div className="at-widget-toolbar at-widget-toolbar--top">
          {dashboardId && (
            <button
              className="at-dash-settings-btn"
              type="button"
              onClick={() => setShowDashboardSettings(true)}
              title="Dashboard settings"
              aria-label="Dashboard settings"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              <span>Manage</span>
            </button>
          )}
        </div>
      )}

      <div className="at-two-col">

        {/* Spacer for centering form when checklist is on right */}
        {activeMode && <div className="at-col-spacer" />}

        {/* ════════════ LEFT COLUMN ════════════ */}
        <div className="at-col-left">

          {/* ──── ACTIVE MODE: Add Transaction ──── */}
          {activeMode === 'add_transaction' && (
            <>
              <button
                className="at-back-btn at-stagger-1"
                type="button"
                onClick={() => {
                  setActiveMode(null);
                  // Notify the parent (Dashboard) so the mobile view returns
                  // to the MobileDashboard tile picker. Without this, the
                  // parent's mobileDashMode stays set and we'd keep
                  // rendering AddTransaction in desktop layout.
                  if (onActiveModeChange) onActiveModeChange(null);
                }}
              >
                ← Back
              </button>
              <form className="at-form" onSubmit={handleSubmit} noValidate>

                {/* ─────── DEBIT / CREDIT TOGGLE ─────── */}
                <div className="at-card at-toggle-card at-stagger-1">
                  <div className="at-toggle-track">
                    <button
                      type="button"
                      className={`at-toggle-btn${txType === 'debit' ? ' active debit' : ''}`}
                      onClick={() => setTxType('debit')}
                    >
                      <span className="at-toggle-icon">↗</span> Debit
                    </button>
                    <button
                      type="button"
                      className={`at-toggle-btn${txType === 'credit' ? ' active credit' : ''}`}
                      onClick={() => setTxType('credit')}
                    >
                      <span className="at-toggle-icon">↙</span> Credit
                    </button>
                  </div>
                  <span className="at-toggle-hint">
                    {txType === 'debit' ? 'Money going out' : 'Money coming in'}
                  </span>
                  <button
                    type="button"
                    className={`at-reload-btn${reloading ? ' spinning' : ''}`}
                    onClick={handleReloadFormData}
                    disabled={reloading}
                    aria-label="Reload form data"
                    title="Reload dashboards, accounts and form data from server"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10" />
                      <polyline points="1 20 1 14 7 14" />
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                    </svg>
                  </button>
                </div>

                {/* ─────── AMOUNT HERO ─────── */}
                <div ref={formRefs.amount} className={`at-card at-amount-hero at-stagger-2 ${txType === 'credit' ? 'at-hero-credit' : ''}`}>
                  <span className="at-currency-sym">{sym}</span>
                  <input
                    className="at-amount-input"
                    type="number"
                    inputMode="decimal"
                    min="0.01"
                    step="0.01"
                    placeholder="0.00"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                  />
                </div>

                {/* ─────── WORKSPACE ─────── */}
                <div ref={formRefs.dashboard} className="at-card at-stagger-3">
                  <div className="at-section-hd">Workspace</div>

                  <div className="at-field">
                    <label className="at-lbl">Dashboard</label>
                    <div className="at-row-controls">
                      {loadingBoards ? (
                        <div className="at-skel" />
                      ) : dashboards.length === 0 ? (
                        <span className="at-hint">No dashboards yet</span>
                      ) : (
                        <select 
                          className="at-sel" 
                          value={dashboardId} 
                          onChange={e => setDashboardId(e.target.value)}
                        >
                          {dashboards.map(d => (
                            <option key={d.id} value={d.id}>{d.name} {d.role === 'owner' ? '(you)' : `(${d.role})`}</option>
                          ))}
                        </select>
                      )}
                      {!propDashboardId && (
                        <button type="button" className="at-chip-btn" onClick={() => setShowNewBoard(v => !v)}>
                          {showNewBoard ? '✕' : '+ New'}
                        </button>
                      )}
                    </div>
                    {showNewBoard && (
                      <div className="at-inline-form">
                        <input
                          className="at-inp"
                          placeholder="Dashboard name  (e.g. Personal, Goa Trip)"
                          value={newBoardName}
                          onChange={e => setNewBoardName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleCreateDashboard())}
                        />
                        <button type="button" className="at-create-btn" onClick={handleCreateDashboard} disabled={creatingBoard}>
                          {creatingBoard ? '...' : 'Create'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Split group */}
                  <div className="at-field at-field-mt">
                    <label className="at-lbl">
                      Split Group
                      <span className="at-optional">optional</span>
                    </label>
                    <div className="at-row-controls">
                      <select
                        className="at-sel"
                        value={splitId}
                        onChange={e => setSplitId(e.target.value)}
                        disabled={!dashboardId}
                      >
                        <option value="">None — personal expense</option>
                        {(qSplitGroups || []).map(g => (
                          <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                      </select>
                      <button type="button" className="at-chip-btn" onClick={() => setShowNewSplit(v => !v)}>
                        {showNewSplit ? '✕' : '+ New'}
                      </button>
                    </div>
                    {showNewSplit && (
                      <div className="at-inline-form">
                        <input
                          className="at-inp"
                          placeholder="Group name  (e.g. Roommates, Goa Trip)"
                          value={newSplitName}
                          onChange={e => setNewSplitName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleCreateSplitGroup())}
                        />
                        <button type="button" className="at-create-btn" onClick={handleCreateSplitGroup} disabled={creatingSplit}>
                          {creatingSplit ? '...' : 'Create'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Tag */}
                  <div className="at-field at-field-mt">
                    <label className="at-lbl">
                      Tag
                      <span className="at-optional">optional</span>
                    </label>
                    <div className="at-row-controls">
                      <select
                        className="at-sel"
                        value={tagId}
                        onChange={e => setTagId(e.target.value)}
                        disabled={!dashboardId}
                      >
                        <option value="">None</option>
                        {tags.filter(t => t.is_active || t.id === tagId).map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                      <button type="button" className="at-chip-btn" onClick={() => setShowNewTag(v => !v)}>
                        {showNewTag ? '✕' : '+ New'}
                      </button>
                      <button type="button" className="at-chip-btn" title="Manage Tags" onClick={() => setTagMgrOpen(v => !v)}>
                        ⚙️
                      </button>
                    </div>
                    
                    {showNewTag && (
                      <div className="at-inline-form">
                        <input
                          className="at-inp"
                          placeholder="Tag name  (e.g. Gift, Travel)"
                          value={newTagName}
                          onChange={e => setNewTagName(e.target.value)}
                        />
                        <input 
                          type="color" 
                          className="at-color-inp" 
                          value={newTagColor} 
                          onChange={e => setNewTagColor(e.target.value)} 
                          title="Choose tag color"
                        />
                        <button type="button" className="at-create-btn" onClick={handleCreateTag}>
                          Create
                        </button>
                      </div>
                    )}

                    {tagMgrOpen && (
                      <div className="at-tag-mgr">
                        <div className="at-tag-mgr-hd">Manage Tags</div>
                        {tags.length === 0 ? <div className="at-hint">No tags yet</div> : (
                          <div className="at-tag-list">
                            {tags.map(t => (
                              <div key={t.id} className={`at-tag-row ${!t.is_active ? 'hidden' : ''}`}>
                                <div className="at-tag-info">
                                  <span className="at-tag-dot" style={{ background: t.color }} />
                                  <span className="at-tag-name">{t.name}</span>
                                </div>
                                <div className="at-tag-actions">
                                  <button type="button" className="at-tag-action" onClick={() => toggleTagActive(t)} title={t.is_active ? 'Hide Tag' : 'Unhide Tag'}>
                                    {t.is_active ? '👁️' : '🕶️'}
                                  </button>
                                  <button type="button" className="at-tag-action at-tag-del" onClick={() => handleDeleteTag(t.id)} title="Delete Tag">
                                    🗑️
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* ─────── CATEGORY ─────── */}
                <div ref={formRefs.category} className="at-card at-stagger-4">
                  <div className="at-section-hd">Category</div>
                  {Object.entries(CATEGORIES).map(([group, cats]) => (
                    <div key={group} className="at-cat-group">
                      <div className="at-cat-group-lbl">{group}</div>
                      <div className="at-cat-grid">
                        {cats.map(c => (
                          <button
                            key={c.id}
                            type="button"
                            className={`at-cat-pill${category === c.id ? ' active' : ''}`}
                            onClick={() => setCategory(c.id)}
                          >
                            <span className="at-cat-em">{c.icon}</span>
                            <span className="at-cat-txt">{c.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* ─────── ACCOUNT / CARD ─────── */}
                <div ref={formRefs.account} className="at-card at-stagger-5">
                  <div className="at-section-hd">Account / Card</div>
                  {false ? (
                    <div className="at-skel" style={{ height: 80 }} />
                  ) : (
                    <div className="at-cards-scroller">
                      {!addableAccounts.some(a => a.account_type === 'cash') && (() => {
                        const sel = accountId === '__cash__';
                        const amt = parseFloat(amount) || 0;
                        const delta = sel && amt > 0 ? (txType === 'debit' ? -amt : amt) : 0;
                        return (
                          <button
                            type="button"
                            className={`at-acc-card${sel ? ' active' : ''}`}
                            style={{ '--acc-color': '#dcfce7' }}
                            onClick={() => setAccountId('__cash__')}
                          >
                            <div className="at-acc-header-row">
                              <span className="at-acc-ico-row">💵</span>
                              <span className="at-acc-type-row">Cash</span>
                            </div>
                            <div className="at-acc-bal-row">{sym}0</div>
                            <div className={`at-acc-delta-row ${delta < 0 ? 'neg' : 'pos'}`}>
                              {delta !== 0 ? `${sym}${Math.abs(delta).toFixed(0)}` : '—'}
                            </div>
                            <div className="at-acc-name-row">Cash</div>
                          </button>
                        );
                      })()}
                      {addableAccounts.map(acc => {
                        const meta = ACCOUNT_META[acc.account_type] || ACCOUNT_META.debit;
                        const isCredit = acc.mechanism === 'pay_later';
                        const sel = accountId === acc.id;
                        const amt = parseFloat(amount) || 0;
                        const bal = parseFloat(acc.current_balance || 0);
                        const delta = sel && amt > 0
                          ? (txType === 'debit' ? (isCredit ? amt : -amt) : (isCredit ? -amt : amt))
                          : 0;
                          return (
                            <button
                              key={acc.id}
                              type="button"
                              className={`at-acc-card${sel ? ' active' : ''}`}
                              style={{ '--acc-color': meta.color }}
                              onClick={() => setAccountId(acc.id)}
                            >
                              <div className="at-acc-header-row">
                                <span className="at-acc-ico-row">{meta.icon}</span>
                                <span className="at-acc-type-row">{meta.label}</span>
                              </div>
                              <div className="at-acc-bal-row">{sym}{Math.abs(bal).toFixed(0)}</div>
                              <div className={`at-acc-delta-row ${delta < 0 ? 'neg' : 'pos'}`}>
                                {delta !== 0 ? `${sym}${Math.abs(delta).toFixed(0)}` : '—'}
                              </div>
                              <div className="at-acc-name-row">{acc.name}</div>
                            </button>
                          );
                      })}
                      <button
                        type="button"
                        className="at-acc-card at-acc-add"
                        onClick={() => setShowNewAcc(v => !v)}
                      >
                        <div className="at-acc-header-row">
                          <span className="at-acc-ico-row">{showNewAcc ? '✕' : '+'}</span>
                          <span className="at-acc-type-row">New</span>
                        </div>
                        <div className="at-acc-bal-row" style={{opacity: 0.3}}>—</div>
                        <div className="at-acc-delta-row" style={{opacity: 0.3}}>—</div>
                        <div className="at-acc-name-row">Add Instrument</div>
                      </button>
                    </div>
                  )}

                  {showNewAcc && (
                    <div className="at-inline-form at-inline-form--acc">
                      <div className="at-type-grid">
                        {ADDABLE_ACCOUNT_TYPES.map(t => (
                          <button
                            key={t.type}
                            type="button"
                            className={`at-type-pill${accType === t.type ? ' active' : ''}`}
                            onClick={() => setAccField('account_type', t.type)}
                          >
                            {t.icon} {t.label}
                          </button>
                        ))}
                      </div>
                      <input
                        className="at-inp"
                        placeholder={accType === 'cash' ? 'Label  (e.g. Wallet Cash)' : 'Card / account name  (e.g. HDFC Salary)'}
                        value={newAcc.name}
                        onChange={e => setAccField('name', e.target.value)}
                      />
                      {showBank && (
                        <input className="at-inp" placeholder="Bank name  (e.g. HDFC, SBI, Chase)"
                          value={newAcc.bank_name} onChange={e => setAccField('bank_name', e.target.value)} />
                      )}
                      {showLast4 && (
                        <input className="at-inp" placeholder="Last 4 digits  (e.g. 4321)" maxLength={4}
                          value={newAcc.card_number_last4} onChange={e => setAccField('card_number_last4', e.target.value.replace(/\D/g, '').slice(0, 4))} />
                      )}
                      {showNetwork && (
                        <div className="at-type-grid">
                          {['Visa', 'Mastercard', 'Rupay', 'Amex', 'Other'].map(n => (
                            <button key={n} type="button"
                              className={`at-type-pill${newAcc.card_network === n ? ' active' : ''}`}
                              onClick={() => setAccField('card_network', n)}>
                              {n}
                            </button>
                          ))}
                        </div>
                      )}
                      {showExpiry && (
                        <div className="at-when-row">
                          <input className="at-inp" type="number" placeholder="Exp Month (1-12)" min="1" max="12"
                            value={newAcc.expiry_month} onChange={e => setAccField('expiry_month', e.target.value)} />
                          <input className="at-inp" type="number" placeholder="Exp Year (e.g. 2029)" min="2024"
                            value={newAcc.expiry_year} onChange={e => setAccField('expiry_year', e.target.value)} />
                        </div>
                      )}
                      {showLimit && (
                        <input className="at-inp" type="number" placeholder="Credit limit"
                          value={newAcc.credit_limit} onChange={e => setAccField('credit_limit', e.target.value)} />
                      )}
                      {showDue && (
                        <input className="at-inp" type="number" placeholder="Billing due day (1-31)" min="1" max="31"
                          value={newAcc.billing_due_day} onChange={e => setAccField('billing_due_day', e.target.value)} />
                      )}
                      {showNotify && (
                        <input className="at-inp" type="number" placeholder="Remind me X days before due (default 3)" min="0" max="30"
                          value={newAcc.notification_days_before} onChange={e => setAccField('notification_days_before', e.target.value)} />
                      )}
                      {showAutoDebit && (
                        <label className="at-check-label">
                          <input type="checkbox" checked={newAcc.auto_debit}
                            onChange={e => setAccField('auto_debit', e.target.checked)} />
                          Auto-debit enabled
                        </label>
                      )}
                      {showProvider && (
                        <input className="at-inp" placeholder="Provider  (e.g. Paytm, Sodexo)"
                          value={newAcc.provider} onChange={e => setAccField('provider', e.target.value)} />
                      )}
                      <input
                        className="at-inp"
                        type="number"
                        placeholder={accType === 'credit' ? 'Current outstanding (0.00)' : 'Opening balance (0.00)'}
                        value={newAcc.initial_balance}
                        onChange={e => setAccField('initial_balance', e.target.value)}
                      />
                      <button type="button" className="at-create-btn" onClick={handleCreateAccount} disabled={creatingAcc}>
                        {creatingAcc ? '...' : (editingAccId ? 'Save Changes' : 'Add Account')}
                      </button>
                    </div>
                  )}
                </div>

                {/* ─────── DATE & TIME ─────── */}
                <div ref={formRefs.when} className="at-card at-when-card at-stagger-6">
                  <div className="at-section-hd">When</div>
                  <div className="at-when-row">
                    <div className="at-when-field">
                      <label className="at-lbl">Date</label>
                      <input type="date" className="at-inp" value={txDate} onChange={e => setTxDate(e.target.value)} />
                    </div>
                    <div className="at-when-field">
                      <label className="at-lbl">Time</label>
                      <input type="time" className="at-inp" value={txTime} onChange={e => setTxTime(e.target.value)} />
                    </div>
                  </div>
                </div>

                {/* ─────── NOTE ─────── */}
                <div ref={formRefs.note} className="at-card at-stagger-7">
                  <div className="at-section-hd">Note</div>
                  <textarea
                    className="at-textarea"
                    placeholder="What is this for? (Required)"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    rows={3}
                    required
                  />
                </div>

                {/* ─────── SUBMIT ─────── */}
                <button
                  type="submit"
                  className={`at-submit at-stagger-8 ${submitting ? ' loading' : ''}${txType === 'credit' ? ' at-submit-credit' : ''}`}
                  disabled={submitting}
                >
                  {submitting ? <span className="at-spinner" /> : txType === 'credit' ? '+ Add Income' : '+ Add Expense'}
                </button>

              </form>
            </>
          )}

          {/* ──── ACTIVE MODE: Pay Credit Card Bill ──── */}
          {activeMode === 'pay_cc_bill' && (
            <>
              <button
                className="at-back-btn at-stagger-1"
                type="button"
                onClick={() => {
                  setActiveMode(null);
                  setPayCCCardId("");
                  setPayCCSourceId("");
                  if (onActiveModeChange) onActiveModeChange(null);
                }}
              >
                ← Back
              </button>
              <div className="at-pay-cc-flow">
                <div className="at-mode-picker-hd">
                  <span className="at-mode-picker-title">Pay Credit Card Bill</span>
                  <span className="at-mode-picker-sub">Quickly settle your credit card outstanding bill</span>
                </div>

              {!payCCCardId ? (
                <div className="at-pay-cc-step">
                  <div className="at-section-hd">Select Credit Card to Pay</div>
                  <div className="at-inst-grid">
                    {realAddableAccounts.filter(a => a.mechanism === 'pay_later').map(acc => {
                      const meta = ACCOUNT_META[acc.account_type] || ACCOUNT_META.debit;
                      return (
                        <button key={acc.id} className="at-inst-tile" onClick={() => { setPayCCCardId(acc.id); setAmount(parseFloat(acc.current_balance || 0).toFixed(2)); }}>
                          <span className="at-inst-tile-glow" style={{ background: meta.color }} />
                          <span className="at-inst-icon">{meta.icon}</span>
                          <div className="at-inst-info">
                            <span className="at-inst-name">{acc.name}</span>
                            <span className="at-inst-bal">{sym}{parseFloat(acc.current_balance || 0).toFixed(2)}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="at-pay-cc-form at-stagger-1">
                   <div className="at-card at-stagger-2">
                     <div className="at-section-hd">1. Board & Amount</div>
                     <div className="at-pay-cc-field-row">
                        <div className="at-pay-cc-field">
                          <label className="at-lbl">Dashboard</label>
                          <select className="at-inp" value={dashboardId} onChange={e => setDashboardId(e.target.value)}>
                            {dashboards.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                          </select>
                        </div>
                        <div className="at-pay-cc-field">
                          <label className="at-lbl">Amount to Pay</label>
                          <input type="number" className="at-inp" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
                        </div>
                     </div>
                   </div>

                   <div className="at-card at-stagger-3">
                     <div className="at-section-hd">2. Source Account (Pay From)</div>
                     <div className="at-inst-grid at-inst-grid--small">
                       {addableAccounts.filter(a => a.id !== payCCCardId).map(acc => {
                         const meta = ACCOUNT_META[acc.account_type] || ACCOUNT_META.debit;
                         const isSel = payCCSourceId === acc.id;
                         return (
                           <button key={acc.id} className={'at-inst-tile ' + (isSel ? 'active' : '')} onClick={() => setPayCCSourceId(acc.id)}>
                             <span className="at-inst-tile-glow" style={{ background: meta.color }} />
                             <span className="at-inst-icon">{meta.icon}</span>
                             <div className="at-inst-info">
                               <span className="at-inst-name">{acc.name}</span>
                               <span className="at-inst-bal">{sym}{parseFloat(acc.current_balance || 0).toFixed(2)}</span>
                             </div>
                           </button>
                         );
                       })}
                     </div>
                   </div>

                   <div className="at-card at-stagger-4">
                      <div className="at-section-hd">When & Note</div>
                      <div className="at-when-row">
                        <div className="at-when-field">
                          <label className="at-lbl">Date</label>
                          <input type="date" className="at-inp" value={txDate} onChange={e => setTxDate(e.target.value)} />
                        </div>
                        <div className="at-when-field">
                          <label className="at-lbl">Time</label>
                          <input type="time" className="at-inp" value={txTime} onChange={e => setTxTime(e.target.value)} />
                        </div>
                      </div>
                      <div style={{ marginTop: 12 }}>
                        <textarea className="at-textarea" placeholder="Note..." value={description} onChange={e => setDescription(e.target.value)} rows={2} />
                      </div>
                   </div>

                   <button className="at-submit at-stagger-5" onClick={handlePayCCBill} disabled={submitting}>
                     {submitting ? 'Processing...' : 'Confirm Payment'}
                   </button>
                   <button className="at-pay-cc-change-btn" type="button" onClick={() => setPayCCCardId("")}>Change Card</button>
                </div>
              )}
              </div>
            </>
          )}


          {/* ──── ACTIVE MODE: Transaction Transfers ──── */}
          {activeMode === 'bank_transfer' && (
            <>
              <button
                className="at-back-btn at-stagger-1"
                type="button"
                onClick={() => {
                  setActiveMode(null);
                  setTransferFromId('');
                  setTransferToId('');
                  if (onActiveModeChange) onActiveModeChange(null);
                }}
              >
                ← Back
              </button>
              <div className="at-pay-cc-flow">
                <div className="at-mode-picker-hd">
                  <span className="at-mode-picker-title">Transaction Transfers</span>
                  <span className="at-mode-picker-sub">Move money between your accounts or cards</span>
                </div>

              {!transferFromId ? (
                <div className="at-pay-cc-step">
                  <div className="at-section-hd">Select Source Account (Transfer From)</div>
                  <div className="at-inst-grid">
                    {addableAccounts.map(acc => {
                      const meta = ACCOUNT_META[acc.account_type] || ACCOUNT_META.debit;
                      return (
                        <button key={acc.id} className="at-inst-tile" onClick={() => setTransferFromId(acc.id)}>
                          <span className="at-inst-tile-glow" style={{ background: meta.color }} />
                          <span className="at-inst-icon">{meta.icon}</span>
                          <div className="at-inst-info">
                            <span className="at-inst-name">{acc.name}</span>
                            <span className="at-inst-bal">{sym}{parseFloat(acc.current_balance || 0).toFixed(2)}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="at-pay-cc-form at-stagger-1">
                  <div className="at-card at-stagger-2">
                    <div className="at-section-hd">1. Dashboard & Amount</div>
                    <div className="at-pay-cc-field-row">
                      <div className="at-pay-cc-field">
                        <label className="at-lbl">Dashboard</label>
                        <select className="at-inp" value={dashboardId} onChange={e => setDashboardId(e.target.value)}>
                          {dashboards.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                      </div>
                      <div className="at-pay-cc-field">
                        <label className="at-lbl">Amount</label>
                        <input type="number" className="at-inp" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" min="0" />
                      </div>
                    </div>
                  </div>

                  <div className="at-card at-stagger-3">
                    <div className="at-section-hd">2. Transfer To</div>
                    <div className="at-inst-grid at-inst-grid--small">
                      {addableAccounts.filter(a => a.id !== transferFromId).map(acc => {
                        const meta = ACCOUNT_META[acc.account_type] || ACCOUNT_META.debit;
                        const isSel = transferToId === acc.id;
                        return (
                          <button key={acc.id} className={'at-inst-tile ' + (isSel ? 'active' : '')} onClick={() => setTransferToId(acc.id)}>
                            <span className="at-inst-tile-glow" style={{ background: meta.color }} />
                            <span className="at-inst-icon">{meta.icon}</span>
                            <div className="at-inst-info">
                              <span className="at-inst-name">{acc.name}</span>
                              <span className="at-inst-bal">{sym}{parseFloat(acc.current_balance || 0).toFixed(2)}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="at-card at-stagger-4">
                    <div className="at-section-hd">When & Note</div>
                    <div className="at-when-row">
                      <div className="at-when-field">
                        <label className="at-lbl">Date</label>
                        <input type="date" className="at-inp" value={txDate} onChange={e => setTxDate(e.target.value)} />
                      </div>
                      <div className="at-when-field">
                        <label className="at-lbl">Time</label>
                        <input type="time" className="at-inp" value={txTime} onChange={e => setTxTime(e.target.value)} />
                      </div>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <textarea className="at-textarea" placeholder="Note (optional)..." value={description} onChange={e => setDescription(e.target.value)} rows={2} />
                    </div>
                  </div>

                  {transferToId && (
                    <div className="at-card at-stagger-5" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #ede9fe 100%)', border: '1.5px solid #93c5fd' }}>
                      {(() => {
                        const from = accounts.find(a => a.id === transferFromId);
                        const to = accounts.find(a => a.id === transferToId);
                        const fromMeta = ACCOUNT_META[from?.account_type] || ACCOUNT_META.debit;
                        const toMeta = ACCOUNT_META[to?.account_type] || ACCOUNT_META.debit;
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 14, justifyContent: 'center', padding: '6px 0', flexWrap: 'wrap' }}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: 24 }}>{fromMeta.icon}</div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{from?.name}</div>
                              <div style={{ fontSize: 11, color: '#64748b' }}>From</div>
                            </div>
                            <div style={{ fontSize: 22, color: '#6c63ff', fontWeight: 800 }}>→</div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: 24 }}>{toMeta.icon}</div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{to?.name}</div>
                              <div style={{ fontSize: 11, color: '#64748b' }}>To</div>
                            </div>
                            {amount && parseFloat(amount) > 0 && (
                              <>
                                <div style={{ fontSize: 18, color: '#6c63ff', fontWeight: 700, margin: '0 4px' }}>·</div>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ fontSize: 20, fontWeight: 800, color: '#2563eb' }}>{sym}{parseFloat(amount).toFixed(2)}</div>
                                  <div style={{ fontSize: 11, color: '#64748b' }}>Amount</div>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  <button className="at-submit at-stagger-6" onClick={handleTransfer} disabled={submitting}>
                    {submitting ? 'Processing...' : '🔄 Confirm Transfer'}
                  </button>
                  <button className="at-pay-cc-change-btn" type="button" onClick={() => { setTransferFromId(''); setTransferToId(''); }}>Change Source</button>
                </div>
              )}
              </div>
            </>
          )}

          {/* ──── ACTIVE MODE: Scheduled Transactions ──── */}
          {activeMode === 'scheduled' && (
            <>
              <button
                className="at-back-btn at-stagger-1"
                type="button"
                onClick={() => {
                  setActiveMode(null);
                  resetSchedState();
                  setAmount('');
                  setCategory('');
                  setDescription('');
                  if (onActiveModeChange) onActiveModeChange(null);
                }}
              >
                ← Back
              </button>
              <div className="at-pay-cc-flow">

              <div className="at-mode-picker-hd">
                <span className="at-mode-picker-title">
                  {category === 'investments' ? 'Mutual Fund SIP' : (editingSchedId ? 'Edit Scheduled Transaction' : 'Scheduled Transactions')}
                </span>
                <span className="at-mode-picker-sub">
                  {category === 'investments' ? 'Set up an automated SIP for your mutual fund portfolio' : (editingSchedId ? 'Update your recurring transaction details' : 'Set up recurring or future-dated transactions')}
                </span>
              </div>

              {/* Step progress indicator */}
              <div className="at-sched-stepper">
                {['Schedule', 'Configure', 'Details'].map((lbl, i) => (
                  <React.Fragment key={lbl}>
                    <div className={`at-sched-step${schedStep === i + 1 ? ' active' : ''}${schedStep > i + 1 ? ' done' : ''}`}>
                      <div className="at-sched-step-dot">{schedStep > i + 1 ? '✓' : i + 1}</div>
                      <div className="at-sched-step-label">{lbl}</div>
                    </div>
                    {i < 2 && <div className="at-sched-step-line" />}
                  </React.Fragment>
                ))}
              </div>

              {/* ── Step 1: Pick recurrence type ── */}
              {schedStep === 1 && (
                <>
                  <div className="at-sched-type-grid">
                    {RECURRENCE_TYPES.map(r => (
                      <button
                        key={r.id}
                        type="button"
                        className={`at-sched-type-tile${schedRecurrence === r.id ? ' active' : ''}`}
                        style={{ '--sched-color': r.color, '--sched-accent': r.accent }}
                        onClick={() => { setSchedRecurrence(r.id); setSchedStep(2); }}
                      >
                        <span className="at-sched-tile-icon">{r.icon}</span>
                        <span className="at-sched-tile-label">{r.label}</span>
                        <span className="at-sched-tile-sub">{r.sub}</span>
                      </button>
                    ))}
                  </div>

                  {/* Existing scheduled transactions list */}
                  {scheduledList.length > 0 && (
                    <div className="at-card" style={{ marginTop: 20 }}>
                      <div className="at-section-hd">Your Scheduled Transactions</div>
                      {loadingScheduled ? <div style={{ color: 'var(--text-sub)', fontSize: 13 }}>Loading…</div> : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {scheduledList.map(s => {
                            const rt = RECURRENCE_TYPES.find(r => r.id === s.recurrence_type);
                            const meta = ACCOUNT_META[s.accounts?.account_type] || ACCOUNT_META.debit;
                            return (
                              <div key={s.id} className="at-sched-list-item" style={{ opacity: s.is_active ? 1 : 0.5 }}>
                                <span className="at-sched-li-icon">{rt?.icon || '🗓️'}</span>
                                <div className="at-sched-li-info">
                                  <div className="at-sched-li-title">{s.category} · {sym}{parseFloat(s.amount).toFixed(0)}</div>
                                  <div className="at-sched-li-sub">{rt?.label} · {meta.icon} {s.accounts?.name} · Next: {s.next_run_date} {s.run_time && `at ${s.run_time}`}</div>
                                </div>
                                <div className="at-sched-li-actions">
                                  <button type="button" className="at-sched-li-toggle" onClick={() => handleToggleScheduled(s)} title={s.is_active ? 'Pause' : 'Resume'}>
                                    {s.is_active ? '⏸' : '▶'}
                                  </button>
                                  <button type="button" className="at-sched-li-edit" onClick={() => handleEditScheduled(s)} title="Modify">✏️ Modify</button>
                                  <button type="button" className="at-sched-li-del" onClick={() => handleDeleteScheduled(s.id)} title="Delete">✕</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* ── Step 2: Configure schedule ── */}
              {schedStep === 2 && (
                <div className="at-pay-cc-form at-stagger-1">
                  {/* Selected type recap */}
                  {(() => {
                    const rt = RECURRENCE_TYPES.find(r => r.id === schedRecurrence);
                    return (
                      <div className="at-sched-type-recap" style={{ background: rt?.color, borderColor: rt?.accent }}>
                        <span style={{ fontSize: 22 }}>{rt?.icon}</span>
                        <div>
                          <div style={{ fontWeight: 700, color: rt?.accent, fontSize: 15 }}>{rt?.label}</div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>{rt?.sub}</div>
                        </div>
                        <button type="button" className="at-pay-cc-change-btn at-pay-cc-change-btn--inline" onClick={() => setSchedStep(1)}>Change</button>
                      </div>
                    );
                  })()}

                  {/* Day of week picker */}
                  {['weekly', 'biweekly'].includes(schedRecurrence) && (
                    <div className="at-card at-stagger-2">
                      <div className="at-section-hd">Day of Week</div>
                      <div className="at-type-grid">
                        {DAYS_OF_WEEK.map((day, idx) => (
                          <button key={idx} type="button"
                            className={`at-type-pill${schedDayOfWeek === idx ? ' active' : ''}`}
                            onClick={() => setSchedDayOfWeek(idx)}>
                            {day.slice(0, 3)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Day of month picker */}
                  {['monthly_fixed', 'quarterly', 'half_yearly', 'yearly'].includes(schedRecurrence) && (
                    <div className="at-card at-stagger-2">
                      <div className="at-section-hd">Day of Month</div>
                      <div className="at-sched-day-grid">
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                          <button key={d} type="button"
                            className={`at-sched-day-btn${schedDayOfMonth === d ? ' active' : ''}`}
                            onClick={() => setSchedDayOfMonth(d)}>
                            {d}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Month picker (yearly only) */}
                  {schedRecurrence === 'yearly' && (
                    <div className="at-card at-stagger-3">
                      <div className="at-section-hd">Month</div>
                      <div className="at-type-grid">
                        {MONTHS_LIST.map((m, idx) => (
                          <button key={idx} type="button"
                            className={`at-type-pill${schedMonth === idx + 1 ? ' active' : ''}`}
                            onClick={() => setSchedMonth(idx + 1)}>
                            {m.slice(0, 3)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Start date + run time */}
                  <div className="at-card at-stagger-3">
                    <div className="at-section-hd">Start Date & Time</div>
                    <div className="at-when-row">
                      <div className="at-when-field">
                        <label className="at-lbl">Date</label>
                        <input type="date" className="at-inp" value={schedStartDate} onChange={e => setSchedStartDate(e.target.value)} min={todayStr()} />
                      </div>
                      <div className="at-when-field">
                        <label className="at-lbl">Run at</label>
                        <input type="time" className="at-inp" value={schedRunTime} onChange={e => setSchedRunTime(e.target.value)} />
                      </div>
                    </div>
                  </div>

                  {/* End condition (not for one-time) */}
                  {schedRecurrence !== 'one_time' && (
                    <div className="at-card at-stagger-4">
                      <div className="at-section-hd">End Condition</div>
                      <div className="at-toggle-track" style={{ marginBottom: 12 }}>
                        {[['never', '♾️ Never'], ['date', '📅 Until Date'], ['count', '🔢 # of Times']].map(([val, lbl]) => (
                          <button key={val} type="button"
                            className={`at-toggle-btn${schedEndType === val ? ' active debit' : ''}`}
                            onClick={() => setSchedEndType(val)}>
                            {lbl}
                          </button>
                        ))}
                      </div>
                      {schedEndType === 'date' && (
                        <input type="date" className="at-inp" value={schedEndDate} onChange={e => setSchedEndDate(e.target.value)} min={schedStartDate} />
                      )}
                      {schedEndType === 'count' && (
                        <input type="number" className="at-inp" placeholder="e.g. 12" min="1" value={schedMaxOcc} onChange={e => setSchedMaxOcc(e.target.value)} />
                      )}
                    </div>
                  )}

                  <button className="at-submit at-stagger-5" type="button" onClick={() => setSchedStep(3)}>
                    Next: Add Details →
                  </button>
                </div>
              )}

              {/* ── Step 3: Transaction details ── */}
              {schedStep === 3 && (
                <div className="at-pay-cc-form at-stagger-1">
                  {/* Debit / Credit toggle */}
                  <div className="at-card at-toggle-card at-stagger-1">
                    <div className="at-toggle-track">
                      <button type="button" className={`at-toggle-btn${txType === 'debit' ? ' active debit' : ''}`} onClick={() => { setTxType('debit'); setCategory(''); }}>
                        <span className="at-toggle-icon">↗</span> Debit
                      </button>
                      <button type="button" className={`at-toggle-btn${txType === 'credit' ? ' active credit' : ''}`} onClick={() => { setTxType('credit'); setCategory(''); }}>
                        <span className="at-toggle-icon">↙</span> Credit
                      </button>
                    </div>
                    <span className="at-toggle-hint">{txType === 'debit' ? 'Money going out' : 'Money coming in'}</span>
                  </div>

                  {/* Amount */}
                  <div className="at-card at-amount-card at-stagger-2">
                    <div className="at-amount-row">
                      <span className="at-currency-sym">{sym}</span>
                      <input type="number" className="at-amount-inp" placeholder="0.00" min="0" value={amount} onChange={e => setAmount(e.target.value)} />
                    </div>
                  </div>

                  {/* Dashboard */}
                  <div className="at-card at-stagger-3">
                    <label className="at-lbl">Dashboard</label>
                    <select className="at-inp" value={dashboardId} onChange={e => setDashboardId(e.target.value)}>
                      {dashboards.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>

                  {/* Category */}
                  <div className="at-card at-stagger-4">
                    <div className="at-section-hd">Category</div>
                    {Object.entries(CATEGORIES).map(([grp, cats]) => (
                      <div key={grp} className="at-cat-group">
                        <div className="at-cat-group-label">{grp}</div>
                        <div className="at-cat-pills">
                          {cats.map(c => (
                            <button key={c.id} type="button"
                              className={`at-cat-pill${category === c.id ? ' active' : ''}`}
                              onClick={() => setCategory(c.id)}>
                              {c.icon} {c.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Account */}
                  <div className="at-card at-stagger-5">
                    <div className="at-section-hd">Account</div>
                    <div className="at-acc-scroller">
                      {addableAccounts.map(acc => {
                        const meta = ACCOUNT_META[acc.account_type] || ACCOUNT_META.debit;
                        return (
                          <button key={acc.id} type="button"
                            className={`at-acc-card${accountId === acc.id ? ' active' : ''}`}
                            style={{ '--acc-color': meta.color }}
                            onClick={() => setAccountId(acc.id)}>
                            <span className="at-acc-icon">{meta.icon}</span>
                            <span className="at-acc-name">{acc.name}</span>
                            <span className="at-acc-bal">{sym}{parseFloat(acc.current_balance || 0).toFixed(0)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Note */}
                  <div className="at-card at-stagger-6">
                    <label className="at-lbl">Note</label>
                    <textarea className="at-textarea" placeholder="e.g. Monthly rent payment…" rows={2} value={description} onChange={e => setDescription(e.target.value)} required />
                  </div>

                  {/* Schedule summary */}
                  {(() => {
                    const rt = RECURRENCE_TYPES.find(r => r.id === schedRecurrence);
                    const ordinal = n => n + (['st','nd','rd'][((n+90)%100-10)%10-1] || 'th');
                    return (
                      <div className="at-card at-stagger-7" style={{ 
                        background: isDark ? 'linear-gradient(135deg, #1e1b4b 0%, #1e1b1e 100%)' : 'linear-gradient(135deg, #ede9fe 0%, #dbeafe 100%)', 
                        border: isDark ? '1.5px solid #4338ca' : '1.5px solid #a5b4fc' 
                      }}>
                        <div className="at-section-hd" style={{ color: isDark ? '#a78bfa' : '#6c63ff' }}>Schedule Summary</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 14 }}>
                          <div>
                            <strong>{rt?.icon} {rt?.label}</strong>
                            {schedRecurrence === 'weekly'      && ` · Every ${DAYS_OF_WEEK[schedDayOfWeek]}`}
                            {schedRecurrence === 'biweekly'    && ` · Every other ${DAYS_OF_WEEK[schedDayOfWeek]}`}
                            {['monthly_fixed','quarterly','half_yearly'].includes(schedRecurrence) && ` · ${ordinal(schedDayOfMonth)} of month`}
                            {schedRecurrence === 'yearly'      && ` · ${ordinal(schedDayOfMonth)} ${MONTHS_LIST[schedMonth - 1]}`}
                          </div>
                          <div style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b' }}>Starts: <strong>{schedStartDate}</strong> at <strong>{schedRunTime}</strong></div>
                          {schedEndType === 'date'  && schedEndDate  && <div style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b' }}>Ends: <strong>{schedEndDate}</strong></div>}
                          {schedEndType === 'count' && schedMaxOcc   && <div style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b' }}>Runs: <strong>{schedMaxOcc} times</strong></div>}
                          {schedEndType === 'never'                   && <div style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b' }}>Runs: <strong>Indefinitely</strong></div>}
                        </div>
                      </div>
                    );
                  })()}

                  <button className="at-submit at-stagger-8" type="button" onClick={handleCreateScheduled} disabled={submitting}>
                    {submitting ? 'Saving…' : (editingSchedId ? '💾 Save Changes' : '🗓️ Schedule Transaction')}
                  </button>
                  <button className="at-pay-cc-change-btn" type="button" onClick={() => setSchedStep(2)}>
                    ← Back to Configure
                  </button>
                </div>
              )}
              </div>
            </>
          )}

          {/* ──── ACTIVE MODE: Manage Instruments ──── */}
          {activeMode === 'manage_instruments' && (
            <>
              <button className="at-back-btn" type="button" onClick={() => { 
                setActiveMode(null); 
                if (onActiveModeChange) onActiveModeChange(null);
                setShowNewAcc(false); 
              }}>← Back</button>
              <div className="at-inst-manager">
                <div className="at-mode-picker-hd">
                  <span className="at-mode-picker-title">Manage Your Instruments</span>
                  <span className="at-mode-picker-sub">View, edit or create new accounts and cards</span>
                </div>

                <div className="at-inst-grid">
                  {realAccounts.map(acc => {
                    const meta = ACCOUNT_META[acc.account_type] || ACCOUNT_META.debit;
                    return (
                      <button 
                        key={acc.id} 
                        className="at-inst-tile"
                        onClick={() => openCardModal(acc)}
                      >
                        <span className="at-inst-tile-glow" style={{ background: meta.color }} />
                        <span className="at-inst-icon">{meta.icon}</span>
                        <div className="at-inst-info">
                          <span className="at-inst-name">{acc.name}</span>
                          <span className="at-inst-bal">{sym}{parseFloat(acc.current_balance || 0).toFixed(2)}</span>
                        </div>
                        <span className="at-inst-type">{meta.label}</span>
                      </button>
                    );
                  })}
                  
                  <button 
                    className={`at-inst-tile at-inst-new-btn ${showNewAcc ? 'active' : ''}`}
                    onClick={() => { if (showNewAcc) { setShowNewAcc(false); setEditingAccId(null); } else { setEditingAccId(null); setNewAcc({name: "", account_type: "debit", initial_balance: "", bank_name: "", card_number_last4: "", card_network: "", expiry_month: "", expiry_year: "", billing_due_day: "", notification_days_before: "3", credit_limit: "", provider: "", auto_debit: false, upi_id: ""}); setShowNewAcc(true); } }}
                  >
                    <span className="at-inst-icon">{showNewAcc ? '✕' : '+'}</span>
                    <span className="at-inst-name">{showNewAcc ? 'Cancel' : 'Create New'}</span>
                  </button>
                </div>

                {showNewAcc && (
                  <div className="at-new-inst-section">
                    <div className="at-section-hd">{editingAccId ? 'Edit Instrument' : 'Add New Instrument'}</div>
                    <div className="at-inline-form at-inline-form--acc">
                      <div className="at-type-grid">
                        {ADDABLE_ACCOUNT_TYPES.map(t => (
                          <button
                            key={t.type}
                            type="button"
                            className={`at-type-pill${accType === t.type ? ' active' : ''}`}
                            onClick={() => setAccField('account_type', t.type)}
                          >
                            {t.icon} {t.label}
                          </button>
                        ))}
                      </div>
                      <input
                        className="at-inp"
                        placeholder={accType === 'cash' ? 'Label  (e.g. Wallet Cash)' : 'Card / account name  (e.g. HDFC Salary)'}
                        value={newAcc.name}
                        onChange={e => setAccField('name', e.target.value)}
                      />
                      {showBank && (
                        <input className="at-inp" placeholder="Bank name  (e.g. HDFC, SBI, Chase)"
                          value={newAcc.bank_name} onChange={e => setAccField('bank_name', e.target.value)} />
                      )}
                      {showUpiId && (
                        <input className="at-inp" placeholder="UPI ID  (e.g. name@okhdfcbank)"
                          value={newAcc.upi_id} onChange={e => setAccField('upi_id', e.target.value)} />
                      )}
                      {showLast4 && (
                        <input className="at-inp" placeholder="Last 4 digits  (e.g. 4321)" maxLength={4}
                          value={newAcc.card_number_last4} onChange={e => setAccField('card_number_last4', e.target.value.replace(/\D/g, '').slice(0, 4))} />
                      )}
                      {showNetwork && (
                        <div className="at-type-grid">
                          {['Visa', 'Mastercard', 'Rupay', 'Amex', 'Other'].map(n => (
                            <button key={n} type="button"
                              className={`at-type-pill${newAcc.card_network === n ? ' active' : ''}`}
                              onClick={() => setAccField('card_network', n)}>
                              {n}
                            </button>
                          ))}
                        </div>
                      )}
                      {showExpiry && (
                        <div className="at-when-row">
                          <input className="at-inp" type="number" placeholder="Exp Month (1-12)" min="1" max="12"
                            value={newAcc.expiry_month} onChange={e => setAccField('expiry_month', e.target.value)} />
                          <input className="at-inp" type="number" placeholder="Exp Year (e.g. 2029)" min="2024"
                            value={newAcc.expiry_year} onChange={e => setAccField('expiry_year', e.target.value)} />
                        </div>
                      )}
                      {showLimit && (
                        <input className="at-inp" type="number" placeholder="Credit limit"
                          value={newAcc.credit_limit} onChange={e => setAccField('credit_limit', e.target.value)} />
                      )}
                      {showDue && (
                        <input className="at-inp" type="number" placeholder="Billing due day (1-31)" min="1" max="31"
                          value={newAcc.billing_due_day} onChange={e => setAccField('billing_due_day', e.target.value)} />
                      )}
                      {showNotify && (
                        <input className="at-inp" type="number" placeholder="Remind me X days before due (default 3)" min="0" max="30"
                          value={newAcc.notification_days_before} onChange={e => setAccField('notification_days_before', e.target.value)} />
                      )}
                      {showAutoDebit && (
                        <label className="at-check-label">
                          <input type="checkbox" checked={newAcc.auto_debit}
                            onChange={e => setAccField('auto_debit', e.target.checked)} />
                          Auto-debit enabled
                        </label>
                      )}
                      {showProvider && (
                        <input className="at-inp" placeholder="Provider  (e.g. Paytm, Sodexo)"
                          value={newAcc.provider} onChange={e => setAccField('provider', e.target.value)} />
                      )}
                      <input
                        className="at-inp"
                        type="number"
                        placeholder={accType === 'credit' ? 'Current outstanding (0.00)' : 'Opening balance (0.00)'}
                        value={newAcc.initial_balance}
                        onChange={e => setAccField('initial_balance', e.target.value)}
                      />
                      <button type="button" className="at-create-btn" onClick={handleCreateAccount} disabled={creatingAcc}>
                        {creatingAcc ? '...' : 'Add Account'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ──── ACTIVE MODE: Split Groups ──── */}
          {activeMode === 'splitgroups' && (
            <div className="at-sg-root">
              <InlineErrorBoundary>
                {/* ── List view ── */}
                {sgView === 'list' && (
                  <>
                    <button className="at-back-btn" type="button" onClick={() => { 
                      setActiveMode(null); 
                      if (onActiveModeChange) onActiveModeChange(null);
                      setSgView('list'); 
                      setShowJoinInput(false); 
                    }}>← Back</button>

                    <div className="at-mode-picker-hd">
                      <span className="at-mode-picker-title">Split Groups</span>
                      <span className="at-mode-picker-sub">Track shared expenses with friends, roommates or coworkers</span>
                    </div>

                    {/* Persistent Action Bar */}
                    <div className="at-sg-actions-row">
                      <button type="button" 
                        className={`at-sg-action-pill ${showJoinInput ? 'active' : ''}`} 
                        onClick={() => { setShowJoinInput(v => !v); setShowNewSplit(false); }}
                      >
                        🔗 Join Group
                      </button>
                      <button type="button" 
                        className={`at-sg-action-pill ${showNewSplit ? 'active' : ''}`} 
                        onClick={() => { setShowNewSplit(v => !v); setShowJoinInput(false); }}
                      >
                        ➕ Create Group
                      </button>
                    </div>

                    {/* Conditional Forms (Inline) */}
                    {showJoinInput && (
                      <div className="at-sg-join-form at-stagger-1" style={{ marginBottom: 20 }}>
                        <input
                          className="at-inp"
                          placeholder="Enter invite code (e.g. AB12CD34)"
                          value={joinCode}
                          onChange={e => setJoinCode(e.target.value.toUpperCase())}
                          onKeyDown={e => e.key === 'Enter' && handleJoinGroup()}
                          maxLength={8}
                          style={{ letterSpacing: '0.12em', fontWeight: 700 }}
                        />
                        <button type="button" className="at-create-btn" onClick={handleJoinGroup} disabled={joiningGroup}>
                          {joiningGroup ? '…' : 'Join'}
                        </button>
                      </div>
                    )}

                    {showNewSplit && (
                      <div className="at-new-inst-section at-stagger-1" style={{ marginBottom: 20 }}>
                        <div className="at-section-hd">New Split Group</div>
                        <div className="at-inline-form">
                          <select className="at-inp" value={dashboardId} onChange={e => setDashboardId(e.target.value)}>
                            {dashboards.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                          </select>
                          <input className="at-inp" placeholder="Group name (e.g. Goa Trip, Flat Expenses)"
                            value={newSplitName} onChange={e => setNewSplitName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleCreateSplitGroup()} />
                          <button type="button" className="at-create-btn" onClick={handleCreateSplitGroup} disabled={creatingSplit}>
                            {creatingSplit ? '…' : 'Create Group'}
                          </button>
                        </div>
                      </div>
                    )}

                    {!showNewSplit && !showJoinInput && (
                      <div className="at-sg-empty" style={{ marginTop: 24 }}>
                        <div className="at-sg-onboarding-grid">
                          <button type="button" 
                            className="at-sg-onboarding-card at-stagger-1" 
                            style={{ background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'var(--glass-bg)', border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : 'var(--glass-border)' }}
                            onClick={() => setShowNewSplit(true)}
                          >
                            <span className="at-sg-onboarding-icon">➕</span>
                            <span className="at-sg-onboarding-title">Create a New Group</span>
                            <span className="at-sg-onboarding-sub">Start a fresh pool with friends or roommates</span>
                          </button>
                          <button type="button" 
                            className="at-sg-onboarding-card at-stagger-2" 
                            style={{ background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'var(--glass-bg)', border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : 'var(--glass-border)' }}
                            onClick={() => setShowJoinInput(true)}
                          >
                            <span className="at-sg-onboarding-icon">🔗</span>
                            <span className="at-sg-onboarding-title">Join Existing Group</span>
                            <span className="at-sg-onboarding-sub">Use an invite code to join a shared pool</span>
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="at-inst-grid" style={{ marginTop: 12 }}>
                      {(Array.isArray(qSplitGroups) ? qSplitGroups : []).map((g, idx) => (
                        <button key={g.id} 
                          className="at-inst-tile at-stagger-1" 
                          style={{ 
                            '--idx': idx, 
                            background: isDark ? 'rgba(255, 255, 255, 0.02)' : 'var(--panel-bg)',
                            borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'var(--border-color)'
                          }}
                          onClick={() => openSplitGroupDetail(g)}
                        >
                          <span className="at-inst-tile-glow" style={{ background: isDark ? 'rgba(108, 99, 255, 0.15)' : '#ede9fe' }} />
                          <span className="at-inst-icon">👥</span>
                          <div className="at-inst-info">
                            <span className="at-inst-name">{g.name}</span>
                            <span className="at-inst-bal" style={{ color: isDark ? '#a78bfa' : '#6c63ff', fontSize: 11 }}>View details →</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {/* ── Detail view ── */}
                {sgView === 'detail' && activeSg && (
                  <div className="at-sg-detail-view at-stagger-1">
                    <button className="at-back-btn" type="button" onClick={() => { setSgView('list'); setActiveSg(null); setSgShowInvite(false); }}>← All Groups</button>

                    <div className="at-mode-picker-hd">
                      <span className="at-mode-picker-title">{activeSg.name}</span>
                      <span className="at-mode-picker-sub">{activeSg.description || 'Shared expenses for this group'}</span>
                    </div>

                    {sgLoadingDetail ? (
                      <div className="at-sg-loading">Opening terminal…</div>
                    ) : (
                      <>
                        {/* Stats row */}
                        <div className="at-sg-stats at-stagger-2">
                          <div className="at-sg-stat">
                            <span className="at-sg-stat-val">{sgMembers.length}</span>
                            <span className="at-sg-stat-lbl">Members</span>
                          </div>
                          <div className="at-sg-stat">
                            <span className="at-sg-stat-val">{sgTxns.length}</span>
                            <span className="at-sg-stat-lbl">History</span>
                          </div>
                          <div className="at-sg-stat">
                            <span className="at-sg-stat-val">{sym}{sgTxns.filter(t => t.transaction_type === 'debit').reduce((s, t) => s + parseFloat(t.amount || 0), 0).toFixed(0)}</span>
                            <span className="at-sg-stat-lbl">Pool Total</span>
                          </div>
                        </div>

                        {/* Members section */}
                        <div className="at-card at-stagger-3" style={{ marginTop: 20 }}>
                          <div className="at-section-hd" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Group Members</span>
                            <button type="button" className="at-chip-btn" onClick={() => { setSgShowInvite(v => !v); if (!activeSg.invite_code) handleGenerateSgInvite(); }}>
                              {sgShowInvite ? '✕ Hide' : '+ Invite'}
                            </button>
                          </div>

                          {sgShowInvite && (
                            <div className="at-sg-invite-box">
                              <div className="at-vs-hd">Invite Friends</div>
                              <span className="at-sg-invite-code">{activeSg.invite_code || '...'}</span>
                              <p style={{ fontSize: 12, opacity: 0.6 }}>Share this code with others to join this group</p>
                            </div>
                          )}

                          <div className="at-sg-members-list">
                            {sgMembers.map((m, idx) => (
                              <div key={m.id} className="at-sg-member-row" style={{'--idx': idx}}>
                                <div className="at-sg-member-info">
                                  <div className="at-sg-member-avatar">{(m.display_name || 'U')[0].toUpperCase()}</div>
                                  <span className="at-sg-member-name">
                                    {m.display_name || 'Unknown'}
                                    {m.id === activeSg.owner_id && <span className="at-sg-member-role">OWNER</span>}
                                  </span>
                                </div>
                                <button type="button" className="at-sched-li-del" onClick={() => handleRemoveSgMember(m.id)} title="Remove">✕</button>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Transactions section */}
                        <div className="at-card at-stagger-4" style={{ marginTop: 20 }}>
                          <div className="at-section-hd">Activity History</div>
                          {sgTxns.length === 0 ? (
                            <div className="at-sg-empty" style={{ padding: '20px 0' }}>No activity recorded yet</div>
                          ) : (
                            <div className="at-sg-tx-list">
                              {sgTxns.map((t, idx) => {
                                const cat = CATEGORIES.find(c => c.id === t.category) || { icon: '📦' };
                                const isDebit = t.transaction_type === 'debit';
                                return (
                                  <div key={t.id} className="at-sg-tx-row" style={{'--idx': idx}}>
                                    <div className="at-sg-tx-left">
                                      <span className="at-sg-tx-icon">{cat.icon}</span>
                                      <div>
                                        <div className="at-sg-tx-cat">{t.category}</div>
                                        <div className="at-sg-tx-sub">{t.accounts?.name} · {t.transaction_date}</div>
                                      </div>
                                    </div>
                                    <span className={`at-sg-tx-amt ${isDebit ? 'debit' : 'credit'}`}>
                                      {isDebit ? '-' : '+'}{sym}{parseFloat(t.amount).toFixed(2)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </InlineErrorBoundary>
            </div>
          )}

          {/* ──── ACTIVE MODE: Lend & Borrow ──── */}
          {activeMode === 'lend_borrow' && (
            <>
              <button
                className="at-back-btn at-stagger-1"
                type="button"
                onClick={() => {
                  setActiveMode(null);
                  if (onActiveModeChange) onActiveModeChange(null);
                }}
              >
                ← Back
              </button>
              <LendBorrow dashboardId={dashboardId} />
            </>
          )}

          {/* ──── ACTIVE MODE: Investments ──── */}
          {activeMode === 'investments' && (
            <>
              <button
                className="at-back-btn at-stagger-1"
                type="button"
                onClick={() => {
                  setActiveMode(null);
                  if (onActiveModeChange) onActiveModeChange(null);
                }}
              >
                ← Back
              </button>
              <InvestmentsSection dashboardId={dashboardId} />
            </>
          )}

        </div>{/* end at-col-left */}

        {/* ════════════ RIGHT COLUMN — Summary & Bills ════════════ */}
        <div className="at-col-right">
          {activeMode && !['splitgroups', 'manage_instruments', 'lend_borrow', 'investments', 'password_setup'].includes(activeMode) && (activeMode !== 'scheduled' || schedStep === 3) && (activeMode !== 'pay_cc_bill' || !!payCCCardId) && (activeMode !== 'bank_transfer' || !!transferFromId) && (
            <div className="at-validation-sidebar">
              <div className="at-vs-hd">
                <span>Checklist</span>
                <span className="at-vs-count">{6 - missingPast.length}/6</span>
              </div>
              <div className="at-vs-list">
                {['Amount', 'Dashboard', 'Category', 'Account', 'Date & Time', 'Note'].map(f => {
                  const isMissing = missingPast.includes(f);
                  // Before scroll, show everything as "done" (green ticks) per user request
                  const displayState = atScrolled ? (isMissing ? 'missing' : 'done') : 'done';
                  return (
                    <div key={f} className={`at-vs-item ${displayState} ${atScrolled && isMissing ? 'at-show-alerts' : ''}`}>
                      <span className="at-vs-dot" />
                      <span className="at-vs-lbl">{f}</span>
                      {(atScrolled && isMissing) && <span className="at-vs-alert">Fill this!</span>}
                      {(!atScrolled || !isMissing) && <span className="at-vs-check">✓</span>}
                    </div>
                  );
                })}
              </div>
              {missingPast.length > 0 && (
                <div className="at-vs-hint">
                  Scroll up to fill missing sections
                </div>
              )}
            </div>
          )}

          {!activeMode && (
            <div className="at-dash-shell">
              {/* Sci-fi corner accents */}
              <span className="at-dash-corner tl" aria-hidden="true" />
              <span className="at-dash-corner tr" aria-hidden="true" />
              <span className="at-dash-corner bl" aria-hidden="true" />
              <span className="at-dash-corner br" aria-hidden="true" />

              {/* ─── HERO STRIP: greeting + KPI ribbon ─── */}
              <div className="at-dash-hero">
                <div className="at-dash-hero-greet">
                  <div className="at-dash-hero-time">{greeting},</div>
                  <h1 className="at-dash-hero-name">{user?.name || user?.email?.split('@')[0] || 'User'}!</h1>
                  <div className="at-dash-hero-sub">Welcome back to your financial control center.</div>
                  <div className="at-dash-hero-date">
                    <span className="at-dash-hero-date-ico">📅</span>
                    {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </div>
                </div>

                <div className="at-dash-main-stats">
                  <div className="at-dash-main-stat-card" onClick={() => onShowBreakdown && onShowBreakdown()} style={{ cursor: onShowBreakdown ? 'pointer' : 'default' }}>
                    <div className="at-dash-stat-header">
                      <span className="at-dash-stat-label">Spending</span>
                      {setCountingMode && (
                        <button
                          className={`at-dash-mode-toggle ${countingMode}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCountingMode(m => m === 'items' ? 'bills' : 'items');
                          }}
                          title={
                            countingMode === 'items'
                              ? 'Items mode: every individual purchase counts. Click to switch to Bills.'
                              : 'Bills mode: counts CC bill payments and wallet loads. Click to switch to Items.'
                          }
                        >
                          {countingMode === 'items' ? '🛒 Items' : '🧾 Bills'}
                        </button>
                      )}
                    </div>
                    <div className="at-dash-stat-value neg">
                      {sym}{totalSpent.toFixed(2)}
                      <span className="at-dash-stat-arrow">→</span>
                    </div>
                    <div className="at-dash-stat-footer">
                      Tap to see breakdown
                    </div>
                  </div>

                  <div className="at-dash-secondary-stats">
                    <div className="at-dash-sec-stat">
                      <span className="at-dash-sec-label">Balance</span>
                      <span className={`at-dash-sec-value ${totalBalance >= 0 ? 'pos' : 'neg'}`}>{sym}{totalBalance.toFixed(2)}</span>
                    </div>
                    <div className="at-dash-sec-stat">
                      <span className="at-dash-sec-label">Owed</span>
                      <span className="at-dash-sec-value-owed">{sym}{totalOwed.toFixed(2)}</span>
                    </div>
                    <div className="at-dash-sec-stat">
                      <span className="at-dash-sec-label">Income</span>
                      <span className="at-dash-sec-value pos">{sym}{totalIncome.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ─── BODY: Quick Actions + Instruments side-by-side ─── */}
              <div className="at-dash-body">
                <section className="at-dash-pane at-dash-pane--quick">
                  <div className="at-dash-pane-hd">
                    <span className="at-dash-pane-title">Quick Actions</span>
                    <span className="at-dash-pane-sub">Jump into a flow</span>
                  </div>
                  <div className="at-mode-grid-widget">
                    {MODES.map((m, idx) => (
                      <button
                        key={m.id}
                        className="at-mode-tile-widget"
                        style={{ '--mode-color': m.color, '--mode-accent': m.accent, '--idx': idx }}
                        onClick={() => setActiveMode(m.id)}
                        type="button"
                      >
                        <span className="at-mode-icon-widget">{m.icon}</span>
                        <div className="at-mode-info-widget">
                          <span className="at-mode-label-widget">{m.label}</span>
                          <span className="at-mode-sub-widget">{m.sub}</span>
                        </div>
                        <span className="at-mode-arrow-widget">→</span>
                      </button>
                    ))}
                  </div>
                  <div className="at-widget-footer-links">
                    <button
                      className="at-widget-link-btn"
                      onClick={() => setShowAccountsMgr(true)}
                      type="button"
                    >
                      🏦 Instruments
                    </button>
                    <button
                      className="at-widget-link-btn"
                      onClick={() => setActiveMode('splitgroups')}
                      type="button"
                    >
                      👥 Split Groups
                    </button>
                  </div>
                </section>

                <section className="at-dash-pane at-dash-pane--summary">
                  <div className="at-dash-pane-hd">
                    <span className="at-dash-pane-title">Instruments</span>
                    <span className="at-dash-pane-sub">{realAccounts.length} total</span>
                  </div>
                  <div className="at-summary-instruments">
                    <div className="at-summary-inst-list">
                      {groupedInstruments.slice(0, 5).map(item => {
                        if (item.isGroup) {
                          return (
                            <div key={item.id} className="at-summary-group">
                              <div className="at-summary-group-row" onClick={() => { if (onAccountClick) onAccountClick(item.members.map(m => m.id)); }} style={{ cursor: onAccountClick ? 'pointer' : 'default' }}>
                                <span className="at-summary-inst-ico">📁</span>
                                <span className="at-summary-inst-nm">{item.name}</span>
                                <span className={`at-summary-inst-bal ${item.total >= 0 ? 'pos' : 'neg'}`}>
                                  {sym}{Math.abs(item.total).toFixed(2)}
                                </span>
                              </div>
                              <div className="at-summary-group-members">
                                {item.members.map(acc => {
                                  const meta = ACCOUNT_META[acc.account_type] || ACCOUNT_META.debit;
                                  return (
                                    <div
                                      key={acc.id}
                                      className="at-summary-inst-row indented"
                                      onClick={() => onAccountClick && onAccountClick(acc.id)}
                                      style={{ cursor: onAccountClick ? 'pointer' : 'default' }}
                                    >
                                      <span className="at-summary-inst-ico">{meta.icon}</span>
                                      <span className="at-summary-inst-nm">{acc.name}</span>
                                      <span className={`at-summary-inst-bal ${
                                        acc.mechanism === 'pay_later'
                                          ? (parseFloat(acc.current_balance || 0) > 0 ? 'neg' : 'pos')
                                          : (parseFloat(acc.current_balance || 0) >= 0 ? 'pos' : 'neg')
                                      }`}>
                                        {sym}{Math.abs(parseFloat(acc.current_balance || 0)).toFixed(2)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }

                        const acc = item;
                        const meta = ACCOUNT_META[acc.account_type] || ACCOUNT_META.debit;
                        return (
                          <div
                            key={acc.id}
                            className="at-summary-inst-row"
                            onClick={() => onAccountClick && onAccountClick(acc.id)}
                            style={{ cursor: onAccountClick ? 'pointer' : 'default' }}
                          >
                            <span className="at-summary-inst-ico">{meta.icon}</span>
                            <span className="at-summary-inst-nm">{acc.name}</span>
                            <span className={`at-summary-inst-bal ${
                              acc.mechanism === 'pay_later'
                                ? (parseFloat(acc.current_balance || 0) > 0 ? 'neg' : 'pos')
                                : (parseFloat(acc.current_balance || 0) >= 0 ? 'pos' : 'neg')
                            }`}>
                              {acc.mechanism === 'pay_later' ? '-' : (parseFloat(acc.current_balance || 0) < 0 ? '-' : '')}{sym}{Math.abs(parseFloat(acc.current_balance || 0)).toFixed(2)}
                            </span>
                          </div>
                        );
                      })}
                      {groupedInstruments.length > 5 && (
                        <button
                          type="button"
                          className="at-summary-view-all-btn"
                          onClick={() => setShowAccountsMgr(true)}
                        >
                          Show all {groupedInstruments.length} instruments →
                        </button>
                      )}
                      {groupedInstruments.length === 0 && (
                        <div className="at-summary-empty">No instruments yet</div>
                      )}
                    </div>
                  </div>
                </section>
              </div>

              {/* ─── BILLS: full-width footer pane ─── */}
              <section className="at-dash-pane at-dash-pane--bills">
                <div className="at-dash-pane-hd">
                  <span className="at-dash-pane-title">Bill Payments</span>
                  {totalOwed > 0 ? (
                    <span className="at-dash-pane-sub at-dash-pane-sub--alert">
                      {sym}{totalOwed.toFixed(2)} outstanding
                    </span>
                  ) : (
                    <span className="at-dash-pane-sub">All clear</span>
                  )}
                </div>
                {billAccounts.length === 0 ? (
                  <div className="at-bills-empty">
                    <span className="at-bills-empty-ico">🎉</span>
                    <span>No outstanding bills</span>
                  </div>
                ) : (
                  <div className="at-bills-list">
                    {billAccounts.map(acc => {
                      const meta = ACCOUNT_META[acc.account_type] || ACCOUNT_META.credit;
                      const accountBalance = parseFloat(acc.current_balance || 0);
                      const dueDay = acc.billing_due_day;
                      return (
                        <button
                          key={acc.id}
                          className="at-bill-card"
                          onClick={() => openCardModal(acc)}
                          type="button"
                        >
                          <div className="at-bill-card-stripe" style={{ background: meta.color }} />
                          <div className="at-bill-card-body">
                            <div className="at-bill-card-top">
                              <span className="at-bill-card-ico">{meta.icon}</span>
                              <span className="at-bill-card-name">{acc.name}</span>
                              {acc.card_network && <span className="at-bill-card-network">{acc.card_network}</span>}
                            </div>
                            <div className="at-bill-card-bottom">
                              <span className="at-bill-card-amt">{sym}{accountBalance.toFixed(2)}</span>
                              {dueDay && <span className="at-bill-card-due">Due day {dueDay}</span>}
                            </div>
                            {acc.card_number_last4 && (
                              <span className="at-bill-card-last4">•••• {acc.card_number_last4}</span>
                            )}
                          </div>
                          <span className="at-bill-card-arrow">→</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {realAccounts.filter(a => a.mechanism === 'pay_later' && parseFloat(a.current_balance || 0) <= 0).length > 0 && (
                  <div className="at-bills-clear">
                    <div className="at-bills-clear-hd">Cleared</div>
                    <div className="at-bills-list">
                      {realAccounts.filter(a => a.mechanism === 'pay_later' && parseFloat(a.current_balance || 0) <= 0).map(acc => {
                        const meta = ACCOUNT_META[acc.account_type] || ACCOUNT_META.credit;
                        const accountBalance = parseFloat(acc.current_balance || 0);
                        return (
                          <button key={acc.id} className="at-bill-card at-bill-card--clear" onClick={() => openCardModal(acc)} type="button">
                            <div className="at-bill-card-stripe" style={{ background: meta.color, opacity: 0.4 }} />
                            <div className="at-bill-card-body">
                              <div className="at-bill-card-top">
                                <span className="at-bill-card-ico">{meta.icon}</span>
                                <span className="at-bill-card-name">{acc.name}</span>
                              </div>
                              <div className="at-bill-card-bottom">
                                <span className="at-bill-card-amt" style={{ color: accountBalance < 0 ? 'var(--accent-color)' : '#16a34a' }}>{sym}{Math.abs(accountBalance).toFixed(2)}</span>
                                <span className="at-bill-card-due" style={{ color: '#16a34a' }}>{accountBalance < 0 ? 'Overpaid' : '✓ Paid'}</span>
                              </div>
                            </div>
                            <span className="at-bill-card-arrow">→</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>
            </div>
          )}
        </div>{/* end at-col-right */}

      </div>{/* end at-two-col */}

      {/* ════════════ MOBILE CHECKLIST FAB + SHEET ════════════ */}
      {activeMode && !['splitgroups', 'manage_instruments', 'lend_borrow', 'investments', 'password_setup'].includes(activeMode) && (activeMode !== 'scheduled' || schedStep === 3) && (activeMode !== 'pay_cc_bill' || !!payCCCardId) && (activeMode !== 'bank_transfer' || !!transferFromId) && (() => {
        const fields = ['Amount', 'Dashboard', 'Category', 'Account', 'Date & Time', 'Note'];
        const missingCount = missingPast.length;
        const allDone = missingCount === 0;
        const showAlert = atScrolled && missingCount > 0;
        const fabState = allDone ? 'done' : (showAlert ? 'alert' : 'idle');

        return (
          <>
            <button
              type="button"
              className={`at-mob-fab at-mob-fab-${fabState}`}
              onClick={() => setShowMobileChecklist(v => !v)}
              aria-label={allDone ? 'All fields complete' : `${missingCount} field${missingCount > 1 ? 's' : ''} missing`}
            >
              <span className="at-mob-fab-icon">
                {allDone ? '✓' : '!'}
              </span>
              {!allDone && <span className="at-mob-fab-badge">{missingCount}</span>}
            </button>

            {showMobileChecklist && (
              <div className={`at-mob-sheet-overlay${closingMobileChecklist ? ' closing' : ''}`} onClick={closeMobileChecklist}>
                <div className={`at-mob-sheet${closingMobileChecklist ? ' closing' : ''}`} onClick={e => e.stopPropagation()}>
                  <div className="at-mob-sheet-handle" />
                  <div className="at-mob-sheet-hd">
                    <span>Checklist</span>
                    <span className="at-vs-count">{6 - missingCount}/6</span>
                  </div>
                  <div className="at-vs-list">
                    {fields.map(f => {
                      const isMissing = missingPast.includes(f);
                      const displayState = atScrolled ? (isMissing ? 'missing' : 'done') : 'done';
                      return (
                        <div key={f} className={`at-vs-item ${displayState} ${atScrolled && isMissing ? 'at-show-alerts' : ''}`}>
                          <span className="at-vs-dot" />
                          <span className="at-vs-lbl">{f}</span>
                          {(atScrolled && isMissing) && <span className="at-vs-alert">Fill this!</span>}
                          {(!atScrolled || !isMissing) && <span className="at-vs-check">✓</span>}
                        </div>
                      );
                    })}
                  </div>
                  {missingCount > 0 && (
                    <div className="at-vs-hint">Scroll up to fill missing sections</div>
                  )}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* ════════════ CARD DETAIL MODAL ════════════ */}
      {cardModalAcc && (() => {
        const acc = cardModalAcc;
        const meta = ACCOUNT_META[acc.account_type] || ACCOUNT_META.debit;
        const accountBalance = parseFloat(acc.current_balance || 0);
        const isPayLater = acc.mechanism === 'pay_later';
        return (
          <div className="at-modal-overlay" onClick={closeCardModal}>
            <div className="at-modal" onClick={e => e.stopPropagation()}>
              <button className="at-modal-close" onClick={closeCardModal}>✕</button>

              {/* 3D Card */}
              <div className={`at-3d-scene${cardFlipped ? ' flipped' : ''}`} onClick={() => setCardFlipped(f => !f)}>
                <div className="at-3d-card">
                  {/* Front */}
                  <div className="at-3d-front" style={{ background: `linear-gradient(135deg, ${meta.color}, ${meta.color}dd)` }}>
                    <div className="at-3d-chip" />
                    <div className="at-3d-number">
                      •••• •••• •••• {acc.card_number_last4 || '••••'}
                    </div>
                    <div className="at-3d-row">
                      <div>
                        <div className="at-3d-label">Card Holder</div>
                        <div className="at-3d-value">{acc.name}</div>
                      </div>
                      <div>
                        <div className="at-3d-label">Expires</div>
                        <div className="at-3d-value">
                          {acc.expiry_month ? `${String(acc.expiry_month).padStart(2, '0')}/${String(acc.expiry_year || '').slice(-2)}` : '••/••'}
                        </div>
                      </div>
                      {acc.card_network && (
                        <div className="at-3d-network">{acc.card_network}</div>
                      )}
                    </div>
                    <div className="at-3d-type-badge">{meta.icon} {meta.label}</div>
                  </div>
                  {/* Back */}
                  <div className="at-3d-back" style={{ background: `linear-gradient(135deg, ${meta.color}ee, ${meta.color})` }}>
                    <div className="at-3d-mag-stripe" />
                    <div className="at-3d-back-details">
                      {acc.bank_name && <div className="at-3d-detail-row"><span>Bank</span><span>{acc.bank_name}</span></div>}
                      {acc.card_network && <div className="at-3d-detail-row"><span>Network</span><span>{acc.card_network}</span></div>}
                      <div className="at-3d-detail-row"><span>Type</span><span>{meta.label}</span></div>
                      <div className="at-3d-detail-row"><span>{isPayLater ? 'Outstanding' : 'Balance'}</span><span>{sym}{accountBalance.toFixed(2)}</span></div>
                      {acc.credit_limit && <div className="at-3d-detail-row"><span>Credit Limit</span><span>{sym}{parseFloat(acc.credit_limit).toFixed(2)}</span></div>}
                      {acc.billing_due_day && <div className="at-3d-detail-row"><span>Due Day</span><span>{acc.billing_due_day}</span></div>}
                      {acc.provider && <div className="at-3d-detail-row"><span>Provider</span><span>{acc.provider}</span></div>}
                    </div>
                    <div className="at-3d-flip-hint">tap to flip</div>
                  </div>
                </div>
              </div>
              <div className="at-3d-flip-hint-front">Tap card to flip</div>

              {/* Account Stats */}
              <div className="at-modal-stats">
                <div className="at-modal-stat">
                  <span className="at-modal-stat-label">{isPayLater ? 'Outstanding' : 'Balance'}</span>
                  <span className={`at-modal-stat-value ${isPayLater ? 'neg' : 'pos'}`}>{sym}{accountBalance.toFixed(2)}</span>
                </div>
                {acc.credit_limit && (
                  <div className="at-modal-stat">
                    <span className="at-modal-stat-label">Available</span>
                    <span className="at-modal-stat-value pos">{sym}{(parseFloat(acc.credit_limit) - accountBalance).toFixed(2)}</span>
                  </div>
                )}
                <div className="at-modal-stat">
                  <span className="at-modal-stat-label">Transactions</span>
                  <span className="at-modal-stat-value">{cardModalTxns.length}</span>
                </div>
              </div>

              {/* Transactions list */}
              <div className="at-modal-txns">
                <div className="at-modal-txns-hd">Recent Transactions</div>
                {cardModalLoading ? (
                  <div className="at-skel" style={{ height: 60 }} />
                ) : cardModalTxns.length === 0 ? (
                  <div className="at-modal-txns-empty">No transactions yet</div>
                ) : (
                  <div className="at-modal-txns-list">
                    {cardModalTxns.map(tx => (
                      <button key={tx.id} className="at-modal-txn-row" onClick={() => { console.log('OPENING MODAL FROM CARD', tx); setEditTx(tx); }}>
                        <div className="at-modal-txn-left">
                          <span className="at-modal-txn-desc">{tx.description || 'Transaction'}</span>
                          <span className="at-modal-txn-date">{new Date(tx.transaction_date).toLocaleDateString()}</span>
                        </div>
                        <span className={`at-modal-txn-amt ${tx.transaction_type === 'credit' ? 'pos' : 'neg'}`}>
                          {tx.transaction_type === 'credit' ? '+' : '-'}{sym}{parseFloat(tx.amount).toFixed(2)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {editTx && (
        <TransactionModal
          transaction={editTx}
          onClose={() => setEditTx(null)}
          onUpdate={() => {
            if (activeMode) fetchAllTransactions(dashboardId);
            if (cardModalAcc) fetchCardTransactions(cardModalAcc.id);
          }}
        />
      )}

      {showDashboardSettings && dashboards.find(d => d.id === dashboardId) && (
        <DashboardSettingsModal
          dashboard={dashboards.find(d => d.id === dashboardId)}
          dashboards={dashboards}
          onClose={() => setShowDashboardSettings(false)}
          onChanged={async () => {
            try {
              const { data } = await api.get('/api/dashboards');
              const list = data || [];
              setDashboards(list);
              if (dashboardId && !list.find(d => d.id === dashboardId)) {
                setDashboardId(list[0]?.id || '');
              }
            } catch (_) { /* leave state as-is */ }
          }}
          onSelectDashboard={(id) => setDashboardId(id)}
        />
      )}

      <AccountsManagerModal
        open={showAccountsMgr}
        onClose={() => setShowAccountsMgr(false)}
        accounts={accounts}
        dashboardId={dashboardId}
        onAddNew={() => {
          // Hand off to the existing manage_instruments mode for the full
          // create form (bank fields, last4, expiry, etc.).
          setShowAccountsMgr(false);
          setEditingAccId(null);
          setNewAcc({
            name: '', account_type: 'debit', initial_balance: '', bank_name: '',
            card_number_last4: '', card_network: '', expiry_month: '', expiry_year: '',
            billing_due_day: '', notification_days_before: '3', credit_limit: '',
            provider: '', auto_debit: false, upi_id: '',
          });
          setShowNewAcc(true);
          setActiveMode('manage_instruments');
          if (onActiveModeChange) onActiveModeChange('manage_instruments');
        }}
        onEdit={(acc) => {
          // Open the existing manage_instruments edit form pre-populated.
          setShowAccountsMgr(false);
          setEditingAccId(acc.id);
          setNewAcc({
            name: acc.name || '',
            account_type: acc.account_type || 'debit',
            initial_balance: acc.initial_limit_or_balance ?? '',
            bank_name: acc.bank_name || '',
            card_number_last4: acc.card_number_last4 || '',
            card_network: acc.card_network || '',
            expiry_month: acc.expiry_month ?? '',
            expiry_year: acc.expiry_year ?? '',
            billing_due_day: acc.billing_due_day ?? '',
            notification_days_before: acc.notification_days_before ?? '3',
            credit_limit: acc.credit_limit ?? '',
            provider: acc.provider || '',
            auto_debit: !!acc.auto_debit,
            upi_id: acc.upi_id || '',
          });
          setShowNewAcc(true);
          setActiveMode('manage_instruments');
          if (onActiveModeChange) onActiveModeChange('manage_instruments');
        }}
      />

    </div>
  );
}
